import { makeHoldout } from "./holdout";
import { makeExperimentEnrollment, AA_EXPERIMENT_ID } from "./experiment-enrollment";
import { makeSportsbookTime, sportsbookTimeState, SPORTSBOOK_TIME_FLUSH_MS, SPORTSBOOK_TIME_SAMPLE_MS } from "./sportsbook-time";
import { BriefExposures, briefActionHandler } from "./brief-events";
import { CompanionApiClient } from "./ranking-client";
import { swapBundle } from "./self-update";
import { BetPlacedWatcher } from "./attribution";
import { BettingBusWatcher, placementEvent } from "./betting-bus";
import type { AckResolution, AddVerdict } from "./betting-bus";
import { storeCouponStakeIds, subscribeStoreCoupon } from "./coupon-store";
import { resolveAdd, type StakePair } from "./add-resolution";
import { legIdentities, planRepost } from "./stale-legs";
import { openMatchDims, outcomeDims, suggestionDims, withOutcomeDims } from "./event-dims";
import { DomAdapter, DigitainSource, DigitainSink, DigitainCatalog } from "./adapter";
import { readRouterLocation, tapEntryFrom } from "./route-source";
import { PrematchSource, HORIZON_MINUTES } from "./prematch-source";
import { SignalCollector } from "./signals";
import { isBetBuilder, buildBetBuilderLegs } from "./bet-builder";
import { addBetBuilderToCoupon, priceViaService, trustedFactor } from "./bet-builder-app";
import type { PriceTrace } from "./bet-builder-app";
import { BbSpace, bestSubPairFactor, drainSpace, outcomeKey, prematchDecision, seedServed, settleRender, type SpaceEntry } from "./bb-space";
import { combinedFactor } from "./place-bet";
import { WidgetRenderer } from "./renderer";
import type { AddOutcome } from "./renderer";
import { nextTopId } from "./self-open-trigger";
import { QualifiedSelfOpenController } from "./qualified-self-open-controller";
import { installFeedCapture } from "./feed-capture";
import { detectLocale, watchLocale, t } from "./i18n";
import { SlipHoldTracker, boardsKnown } from "./slip-hold";
import { readCouponTotal, readHostGeometry } from "./host-geometry";
import { diagEnabled, formatAddProbe, startDiag } from "./diag";
import { makeStarGateProbe, starGateLine, STAR_GATE_DELAY_MS, type StarGateRead } from "./star-gate";
import { makeAuthWatch, AUTH_POLL_MS } from "./auth-watch";
import { makeUserHash } from "./user-hash";
import { DigitainFavourites } from "./favourites";
import { DigitainBetHistory } from "./bet-history";
import { isLiveTab } from "./live-tab";
import type { MarketLine } from "./market-source";
import type { InitConfig, LiveMatch, Suggestion } from "./types";
import { adviceSnapshot } from "./advice-snapshot";
import { peekOpenMeta, peekCloseMeta,
  panelCloseMeta, peekNavMeta } from "./interaction-events";
import { eventLegs, makeImpressionSender, makeFactDiagnosticSender } from "./impression";
import { isHidden, markHidden } from "./hide-state";
import { detectOpenMatch, type BoardEntry } from "./open-match-dom";
import { observeOpenMatch, resolveOpenMatch, onOpenMatchObserved, stopOpenMatchObserved, appOpenEventId, appOpenEvent, appStoreReachable, watchOpenEvent } from "./open-match-net";
import { makeOpenWaker } from "./open-wake";
import { SkipMemory } from "./skip-memory";
import { openEventMarketsFor, openEventEntryFor, openEventNamesFor } from "./open-event-markets";
import { shouldWarmSubLines, SUB_WARM_INTERVAL_MS } from "./sub-lines-warm";
import { makeSettlementFlusher } from "./settlement-flush";

// Install the WebSocket odds-feed interceptor BEFORE anything else — the widget's
// bootLandingWidgets opens its socket within ~1-2s of boot, so the hook must be in
// place first (docs/digitain-ws-capture-plan.md, Phase 0). Gated by data-capture="1"
// on the script tag: zero footprint on a normal operator page when off.
(function bootstrapFeedCapture(): void {
  try {
    const el = document.querySelector(
      "script[data-api-key]",
    ) as HTMLScriptElement | null;
    if (el?.getAttribute("data-capture") === "1") {
      installFeedCapture({ capture: true, full: true });
    }
  } catch {
    /* capture must never break init */
  }
})();

let initialized = false;
let activeWidgetCleanup: (() => void) | null = null;

/** The server named this build in `disabled` on /suggestions: tear the widget
 * down and stop. Called from the idle timer, AFTER the poll that set the flag.
 *
 * ⛔ `destroy()`, NOT `renderer.hide()`. `hide()` fires `onHide`, which records a
 * `widget_hide` event and marks the session hidden in `sessionStorage` (see
 * `hide-state.ts`, key `bc_hidden_v1:<apiKey>`) — that is the
 * USER's decision and it must not be forged by a server action. A retire leaves
 * the session's own hide-state untouched, so the next page load (which fetches a
 * fresh bundle — `Cache-Control: max-age=300`) starts clean.
 *
 * ⛔ It does NOT self-reload the page or inject a replacement bundle. Injecting a
 * second copy of the SDK into a live SPA that is mid-bet is a bigger risk than one
 * dark session, and the 5-minute cache header means the next ordinary page load
 * picks up the new build anyway. The lever this gives us is "stop a bad build now",
 * not "upgrade it in place".
 *
 * Idempotent: `destroy()` is, and the flag never clears. */
/** The server names a newer build: swap this bundle for it (self-update.ts).
 * Never while the visitor has a card open — the next idle poll does it. */
export function upgradeWidget(client: CompanionApiClient, renderer: WidgetRenderer): boolean {
  const target = client.upgradeTarget();
  if (!target || renderer.isPeekOpen()) return false;
  // Only a starter that registered its teardown can be swapped: a bare
  // renderer.destroy() would leave that starter's poll running.
  const cleanup = activeWidgetCleanup;
  if (!cleanup) return false;
  return swapBundle(target, cleanup);
}

/** Test seam: register a starter's teardown as the Digitain path does. */
export function __setActiveCleanupForTest(fn: (() => void) | null): void {
  activeWidgetCleanup = fn;
}

export function retireWidget(renderer: WidgetRenderer): void {
  console.warn("[bidcanvas] server retired this SDK build — widget stopped.");
  if (activeWidgetCleanup) activeWidgetCleanup();
  else renderer.destroy();
}

// An `impression` means "the user saw this pick" — one send per suggestion per
// window, not one per poll (per-poll re-sends flooded the rate-limited events
// endpoint and inflated the funnel).
const IMPRESSION_RESEND_MS = 30 * 60 * 1000;

// The verdict window for an add where the ack channel is KNOWN to exist
// (`seenBus`). Parikara's audience is mobile Turkmenistan against Digitain's
// infrastructure — a 2,500 ms clock there wrote slow acks up as refusals. 2,500
// stays right for the no-bus decision, where waiting longer only delays
// Hannibalbet's mint fallback. Re-tune from `betslip_add_late.late_ms` (the
// measured ack latency), not by guessing.
const ACK_TIMEOUT_BUS_MS = 6000;
// How long after a TIMEOUT verdict we keep watching for the acks to arrive
// before letting the resolver's decision touch the coupon.
const LATE_ACK_WINDOW_MS = 15000;

async function init(): Promise<void> {
  if (initialized) return;
  initialized = true;

  // ⛔ AT BOOT, not in startWidget. The app can fetch the event it opens before
  // the widget has finished starting, and startWidget is not reached at all on
  // the operator's casino routes — so installing the observer later loses the
  // very first navigation. Wrapping fetch is cheap and side-effect free.
  observeOpenMatch();

  // Read config from script tag
  const scriptEl = document.querySelector(
    "script[data-api-key]",
  ) as HTMLScriptElement | null;
  if (!scriptEl) return;

  const apiKey = scriptEl.getAttribute("data-api-key");
  if (!apiKey) return;

  // Collector mode (data-collector="1"): a headless server session runs the feed
  // + /suggestions poll to WARM the odds history (momentum), but shows no overlay
  // and emits no user telemetry. Digitain only. See collector/.
  const collector = scriptEl.getAttribute("data-collector") === "1";

  // Derive API base: explicit attribute, or from script src origin
  let apiBase = scriptEl.getAttribute("data-api-base") ?? "";
  if (!apiBase && scriptEl.src) {
    try {
      const url = new URL(scriptEl.src);
      apiBase = url.origin;
    } catch {
      // invalid src URL — give up
      return;
    }
  }
  if (!apiBase) return;

  const client = new CompanionApiClient(apiBase, apiKey);

  // Fetch operator config
  const configResponse = await client.fetchConfig();
  if (!configResponse) return;

  const { config: operatorConfig, adapter: adapterConfig } = configResponse;

  // Drag-to-move persists the widget's spot per operator site.
  // v2: bumped so stale saved drag coords (from earlier default positions) are
  // ignored and the new default applies; the user can re-drag from there.
  const posStorageKey = `bc_widget_pos_v2:${apiKey}`;

  // ⛔ The user closed the widget earlier in this session. Return before ANY path
  // mounts: no container, no renderer, and — the part that matters on a live pilot
  // — no /suggestions poll. A closed widget that keeps polling still costs the
  // capacity that is the binding constraint here, and still counts as a visitor.
  // Collector mode is exempt: it has no UI to close and exists only to warm the
  // odds history. A/A-capable sessions keep measurement running after a user
  // hide, without mounting the widget or polling suggestions.
  const aaTracking = operatorConfig.controlled_experiment?.mode === "aa" &&
    operatorConfig.controlled_experiment.id === AA_EXPERIMENT_ID;
  if (!collector && isHidden(apiKey) && !aaTracking) return;

  // Digitain: event-bus adapter (no DOM scraping). Attach to the SportFrame bus.
  if (adapterConfig.platform === "digitain" && adapterConfig.event_bus) {
    activeWidgetCleanup = startWidgetDigitain(
      client, operatorConfig, adapterConfig.event_bus, posStorageKey, collector, apiKey, !collector && isHidden(apiKey));
    return;
  }

  // SoftSwiss / DOM-scraping adapters.
  const domAdapter = new DomAdapter(adapterConfig);

  // Try to find the sportsbook iframe, retrying up to 10 times
  waitForIframe(domAdapter, client, operatorConfig, posStorageKey, apiKey, 0);
}

function waitForIframe(
  adapter: DomAdapter,
  client: CompanionApiClient,
  operatorConfig: InitConfig["operatorConfig"],
  posStorageKey: string,
  apiKey: string,
  attempt: number,
): void {
  const doc = adapter.getSportsbookDocument(document);
  if (doc) {
    startWidget(adapter, client, operatorConfig, doc, posStorageKey, apiKey);
    return;
  }

  if (attempt < 10) {
    setTimeout(() => waitForIframe(adapter, client, operatorConfig, posStorageKey, apiKey, attempt + 1), 1000);
  }
  // After 10 attempts, silently give up
}

function startWidget(
  adapter: DomAdapter,
  client: CompanionApiClient,
  operatorConfig: InitConfig["operatorConfig"],
  iframeDoc: Document,
  posStorageKey: string,
  apiKey: string,
): void {
  // Create widget container
  const container = document.createElement("div");
  container.id = "bidcanvas-companion";
  document.body.appendChild(container);

  // Auto-detect operator's brand color from CSS variables
  const hostBrand = getComputedStyle(document.documentElement)
    .getPropertyValue("--brand-primary").trim();
  const theme = { ...operatorConfig.theme };
  if (hostBrand && hostBrand.startsWith("#")) {
    theme.primary_color = hostBrand;
  }

  // The language the user picked in the sportsbook — NOT the operator's configured
  // default. A Tunisian book offers EN/FR/AR and the config is only the fallback.
  let locale = detectLocale(operatorConfig.locale);
  client.setLocale(locale);   // ⛔ so every event carries the language

  const renderer = new WidgetRenderer(container, theme, posStorageKey, locale);
  // This path's failed add does NOT navigate, and reports so via its AddOutcome —
  // the renderer defaults to the honest "couldn't add" label.

  let currentSuggestions: Suggestion[] = [];

  const device = window.innerWidth < 768 ? "mobile" : "desktop";

  // Launcher funnel — same as the Digitain path (pill_impression → pill_open).
  renderer.onPillShown = (p) => {
    client.sendEvent("", "pill_impression", "", null, { ...p, device, locale });
  };
  renderer.onPillOpen = (p) => {
    client.sendEvent("", "pill_open", "", null, { ...p, device, locale });
  };
  renderer.onHide = (reason) => {
    markHidden(apiKey);
    client.sendEvent("", "widget_hide", "", null, { reason, device, locale });
  };

  // Wire suggestion click: click the matching odds button in the iframe, and only
  // report `betslip_add` for a click that actually happened — an unconditional
  // event here meant SoftSwiss stats counted adds that never reached the slip.
  renderer.onSuggestionClick = (id: string, shown?: Suggestion): boolean => {
    // Same staleness guard as the Digitain path: the card the user tapped beats a
    // lookup in a list the poll has already replaced.
    const suggestion = currentSuggestions.find((s) => s.id === id) ?? shown;
    if (suggestion && suggestion.type !== "fact") {
      // Find and click the matching odds button in the sportsbook iframe
      const oddsButtons = iframeDoc.querySelectorAll(".sb-Odd");
      for (const btn of oddsButtons) {
        const text = (btn.textContent ?? "").replace(/[^\d.]/g, "");
        if (text === String(suggestion.odds)) {
          (btn as HTMLElement).click();
          client.sendEvent("", "betslip_add", id, null, {
            advice_snapshot: adviceSnapshot(suggestion),
          });
          // In the slip now — retire the card so it can't be skipped afterwards.
          renderer.markAdded(id);
          return true; // clicked the odds → added to the SoftSwiss betslip
        }
      }
    }
    // No button matched — nothing was clicked and nothing navigates on this path;
    // the caller renders the honest "couldn't add" state, not "Opened in sportsbook".
    return false;
  };

  // Wire dismiss
  renderer.onDismiss = (id: string) => {
    client.sendEvent("", "dismiss", id);
  };

  // Signal collector (simple accumulator, no internal timer)
  const collector = new SignalCollector();

  // Listen for clicks inside sportsbook iframe
  try {
    iframeDoc.addEventListener("click", (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link) {
        const href = link.getAttribute("href") ?? "";
        const matchId = adapter.extractMatchId(href);
        if (matchId) {
          collector.add("click", matchId, 1);
        }
      }
    });
  } catch {
    // Cross-origin — can't attach listener
  }

  // Single refresh function with concurrency guard
  let inFlight = false;
  const impressionSent = new Map<string, number>(); // suggestion id → last sent ts

  const refresh = async (): Promise<void> => {
    // ⛔ Closed by the user. Stop polling THIS page view too, not just the next
    // load: /suggestions re-sends the whole board on every tick, and capacity —
    // not per-request cost — is what binds on this pilot. A widget nobody can see
    // must not keep paying for itself.
    if (renderer.isHidden()) return;
    // ⛔ The server retired this build. Same reasoning as the hidden case above,
    // one step harder: not "nobody can see it" but "this code must stop".
    if (client.isRetired()) return;
    if (inFlight) return;
    inFlight = true;
    try {
      const signals = collector.drain();
      const matches = adapter.scrapeMatches(iframeDoc);
      const odds = adapter.scrapeOdds(iframeDoc);

      const suggestions = await client.fetchSuggestions(
        signals,
        {
          live_matches: matches as unknown as Array<Record<string, unknown>>,
          odds_snapshot: odds as unknown as Array<Record<string, unknown>>,
          betslip: [],
        },
        locale,
        device,
      );

      if (suggestions == null) return; // failed poll — keep current DOM as-is
      currentSuggestions = suggestions;
      renderer.update(suggestions);

      // Only report picks the user can actually see (mobile rests as the launcher).
      if (renderer.isCollapsed()) return;
      for (const s of suggestions) {
        // Once per suggestion per window — not per 5s poll (see the Digitain path).
        const lastSent = impressionSent.get(s.id);
        if (lastSent != null && Date.now() - lastSent < IMPRESSION_RESEND_MS) continue;
        impressionSent.set(s.id, Date.now());
        client.sendEvent("", "impression", s.id);
      }
    } finally {
      inFlight = false;
    }
  };

  // Single timer — one in flight, no races. ⛔ Self-rescheduling, NOT a fixed
  // 5 s setInterval: the server tells us how long its answer is good for
  // (`ttl`, 30 s) and we now honour it. The old hardcoded 5 s ignored that and
  // put the live pilot into queue collapse on 2026-08-26 — see
  // `CompanionApiClient.pollIntervalMs`. The event-driven refresh below is
  // deliberately kept: when the user actually browses a new match we still
  // re-rank immediately, throttled by MIN_REFRESH_MS.
  const scheduleIdlePoll = (): void => {
    // Checked BEFORE rescheduling and AFTER the poll that could have set it: the
    // retire signal arrives on a /suggestions response, so this is the first
    // moment it can be true.
    if (client.isRetired()) { retireWidget(renderer); return; }
    // No self-update on this DOM-scraping path: it registers no teardown, so a
    // swap would leave its timers and poll running beside the new bundle. No
    // live operator runs it (Parikara and Hannibalbet are Digitain).
    setTimeout(() => { void refresh(); scheduleIdlePoll(); },
               client.pollIntervalMs(5000));
  };
  scheduleIdlePoll();

  // Follow the user if they switch language. The sportsbook is a SPA, so a switch
  // doesn't reload the page: re-render the chrome in the new language and re-fetch
  // so the ADVICE arrives translated too (a cached English sentence would otherwise
  // sit on a French card until the pick rotated out).
  watchLocale(locale, (next) => {
    locale = next;
    client.setLocale(locale);
    renderer.setLocale(next);
    void refresh();
  }, operatorConfig.locale);

  // Initial fetch with retry (iframe content may still be loading)
  const initialFetch = async (retries: number): Promise<void> => {
    const matches = adapter.scrapeMatches(iframeDoc);
    if (matches.length === 0 && retries > 0) {
      setTimeout(() => void initialFetch(retries - 1), 2000);
      return;
    }
    void refresh();
  };
  void initialFetch(5);
}

// ⛔ THE OPERATOR'S PRICE HAS THE LAST WORD ON A SAME-MATCH ACCA, and this is the
// ONE place that decides what its number means. Measured on the live book
// 2026-09-12 (Наполи–Болонья 40343484), one POST per row, `BBF` = the operator's
// price for exactly the posted selection:
//
//   П1 + Обе-Да                    → 4.15   (best single leg 1.85, product 3.64)
//   + Тотал>2.5                    → 4.15   the third leg pays NOTHING
//   + Тотал Ком.1>1.5              → 4.15   the third leg pays NOTHING
//   + Тотал>3.5                    → 6.80   a real third leg
//   П1 1.85 + 1X 1.23              → 1.85   the second leg pays NOTHING
//
// So: 0 is a refusal, ≤ the best single leg is a leg that pays nothing, and a
// TRIPLE that does not beat its own PAIR is a two-leg card wearing three legs.
// ⛔ Never multiply the legs to find any of this: they are correlated and the
// book prices them lower (1.85 × 2.14 = 3.96 where the operator says 3.05).
// ⚠️ null factor = "could not tell" (slow reply, no board) and is NOT a refusal —
// hiding a card on a timeout empties the deck.
export type BbVerdict = "refused" | "no-value" | "dead-leg" | null;

export function bbVerdict(factor: number | null, bestLeg: number,
                          pairFactor: number | null, legs: number): BbVerdict {
  if (factor == null) return null;
  if (factor === 0) return "refused";
  if (factor <= bestLeg + 0.001) return "no-value";
  if (legs >= 3 && pairFactor != null && factor <= pairFactor + 0.001) return "dead-leg";
  return null;
}

// Digitain event-bus widget: same renderer + ranking, but context comes from the
// SportFrame bus and "Add to Betslip" injects a coupon via setCouponFromBookingNumber.
export function startWidgetDigitain(
  client: CompanionApiClient,
  operatorConfig: InitConfig["operatorConfig"],
  eventBus: NonNullable<InitConfig["adapter"]["event_bus"]>,
  posStorageKey: string,
  collector = false,
  apiKey = "",
  measurementOnly = false, // Honor an existing user hide while keeping A/A telemetry alive.
): () => void {
  const lifecycleIntervals = new Set<ReturnType<typeof setInterval>>();
  const trackInterval = (fn: () => void, ms: number): void => {
    lifecycleIntervals.add(setInterval(fn, ms));
  };
  let stopped = false;
  const source = new DigitainSource(eventBus);      // current event (context)
  const sink = new DigitainSink(eventBus);          // navigate + coupon inject
  const catalog = new DigitainCatalog();            // candidate pool (hidden widget harvest)
  // matchIsLive on the toggleStake post ROUTES Digitain's stake lookup, so the
  // sink must post the leg's REAL liveness: hardcoded `true` sent every
  // pre-match add to the live catalogue, which refused it instantly as
  // ERROR_STAKE_DOS_NOT_EXIST (95% of pre-match adds rejected, measured
  // 2026-09-01; proven by re-posting the same stake id with the flag flipped).
  sink.isEventLive = (eventId) => {
    const f = catalog.eventFacts(eventId);
    return f ? f.is_live : null;
  };
  // The user's own ★ Фавориты, off the operator's own channel. Passive: it reads
  // the three /Account/* responses when their client makes those calls and caches
  // the result for 30 days, so one visit to that tab keeps steering the ranking.
  const favourites = new DigitainFavourites(apiKey);
  favourites.start();
  // What they have actually bet. Passive by default — it only reads the account
  // responses the operator's own UI requests. Active top-up is opt-in per operator
  // (`bet_history_backfill`) because it puts extra load on THEIR API.
  const betHistory = new DigitainBetHistory(apiKey, operatorConfig.bet_history_backfill === true);
  betHistory.start();
  let local: Storage | null = null;
  let session: Storage | null = null;
  try { local = window.localStorage; } catch { /* storage unavailable */ }
  try { session = window.sessionStorage; } catch { /* storage unavailable */ }
  const flushSettlements = makeSettlementFlusher({
    settlements: () => betHistory.settlements(),
    send: (s) => {
      // ⚠️ `locale` rides along ONLY because the market name does: the label is
      // in the language this session fetched its history in, and a name filed
      // under the wrong language is how a Russian label ends up on an English
      // reader's page. Read at send time — the user can switch mid-session.
      void client.sendEvent("", "bet_settled", "", null, { ...s, locale });
    },
    local,
    session,
  });
  trackInterval(flushSettlements, 60_000);

  const container = document.createElement("div");
  container.id = "bidcanvas-companion";
  // Collector mode: keep the container OFF-DOM so nothing renders — the session
  // exists only to drive the feed + /suggestions poll (which warms odds_history).
  if (!collector && !measurementOnly) {
    document.body.appendChild(container);
    // SURVIVE A DOM WIPE. Owner-reported 2026-08-04: coming BACK to the sportsbook
    // from the casino, the overlay was gone and only re-running the bookmarklet
    // brought it back. If their SPA replaces the body on that transition, our host
    // node goes with it while this script keeps running happily against a detached
    // element — every timer alive, nothing on screen, no error anywhere.
    //
    // Re-appending is enough: the shadow root and its whole tree travel with the
    // node, so the chip comes back where it was, with its data. (If instead the
    // transition is a real page load, this cannot help — the script itself is gone
    // — but then a REAL install re-loads with the page and only the bookmarklet
    // demo is affected.)
    trackInterval(() => {
      if (!container.isConnected && document.body) document.body.appendChild(container);
    }, 1000);
  }

  // Auto-detect operator's brand color from CSS variables
  const hostBrand = getComputedStyle(document.documentElement)
    .getPropertyValue("--brand-primary").trim();
  const theme = { ...operatorConfig.theme };
  if (hostBrand && hostBrand.startsWith("#")) {
    theme.primary_color = hostBrand;
  }

  // Hannibalbet offers EN / FR / AR — read what the USER chose, don't infer it from
  // the operator's geo (a Tunisian book has been observed running RU).
  let locale = detectLocale(operatorConfig.locale);
  client.setLocale(locale);   // ⛔ so every event carries the language
  let timeAccount: () => string | null = () => null;
  let holdoutHidden = false;
  let holdoutTick: (refresh?: boolean) => void = () => {};
  let beginExperimentTime = () => {};
  let observeTimeAccount = () => {};
  const experiment = makeExperimentEnrollment(
    collector ? null : operatorConfig.controlled_experiment, () => timeAccount(),
    () => !document.hidden && document.hasFocus() &&
      readHostGeometry(theme.sportsbook_root).sportsbookMounted === true,
    account => {
      void client.sendEvent("", "experiment_presence", "", null, {user_hash: account});
      beginExperimentTime();
    },
  );
  client.setExperimentReader(experiment.experimentFor);
  // Login state (boolean only) on every event + `auth_change` on a transition —
  // what joins an anonymous visitor's use of the widget to a later login/bet.
  {
    const AUTH_KEY = `bc_auth:${apiKey}`;
    const auth = makeAuthWatch(client, () => catalog.loggedIn(), {
      get: () => { try { return sessionStorage.getItem(AUTH_KEY); } catch { return null; } },
      set: (v) => { try { sessionStorage.setItem(AUTH_KEY, v); } catch { /* storage blocked */ } },
    });
    // The stamp is the SETTLED state: a hydrating logged-in app's early false is
    // not stamped, so a session's false stamps really are logged out.
    client.setAuthReader(auth.stamp);
    trackInterval(auth.tick, AUTH_POLL_MS);
    // Account key for MAU (2026-10-05): hashed in the page, only the hash leaves.
    const userHash = makeUserHash(apiKey, () =>
      catalog.betBuilderGate ? catalog.betBuilderGate().userId : null);
    // Returning accounts may already be enrolled on the server before this
    // page has initialized its assignment controller.
    if(!collector && operatorConfig.holdout_capable)
      client.setHoldoutReader(()=>({ab_run:'parikara-ab-v1',ab_state:'pending'}),()=>false);
    client.setUserHashReader(userHash.current);
    timeAccount = userHash.current;
    void userHash.tick().then(() => { holdoutTick(); experiment.tick(); observeTimeAccount(); });
    trackInterval(() => { void userHash.tick().then(() => { holdoutTick(); experiment.tick(); observeTimeAccount(); }); }, AUTH_POLL_MS);
  }
  // ⛔ "on"/"off" STRING, and ABSENT until the control panel sets it — absent
  // means off, which is what keeps a disabled feature from calling the
  // operator's API. ⚠️ Declared in THIS entry point: the first one has no
  // catalog and never calls primeMarkets.
  // ⛔ Arrives as a BOOLEAN from the config whitelist, not the raw "on"/"off"
  // string the setting stores — every non-empty string is truthy in JS.
  const factsPickMarket = Boolean(
    (operatorConfig as unknown as Record<string, unknown>).facts_pick_market);

  const renderer = new WidgetRenderer(container, theme, posStorageKey, locale);
  if (measurementOnly) renderer.destroy();
  let currentSuggestions: Suggestion[] = [];
  const device = window.innerWidth < 768 ? "mobile" : "desktop";
  let lastTopId: string | null = null;
  // Events the LAST response marked `sofa_covered` — the only ones whose
  // live-feed lines are posted (postedLadder). Updated every response.
  let lastCovered = new Set<string>();

  // ⭐ SAME-MATCH ACCAS ARE PRICED BY THE OPERATOR BEFORE THEY ARE SHOWN.
  //
  // A same-match acca's legs are CORRELATED, so multiplying them is not its price.
  // Measured on the live book 2026-09-12 (Наполи–Болонья 40343484):
  //
  //   П1 1.85 + Больше2.5 2.14              → operator 3.05, product 3.96
  //   … + Обе-Да 1.97                       → operator 4.15, product 7.80
  //   П1 1.85 + 1X 1.23   (correlated)      → operator 1.85 — the leg pays NOTHING
  //   П1 + Больше2.5 + 1X                   → operator 3.05 — same, the 3rd is free
  //
  // ⛔ So there are two ways a card can lie, and only the operator can tell us:
  // the price (fix: show `BBF`), and a leg that adds nothing (fix: drop the card —
  // `BBF` no greater than the best single leg means at least one leg is decoration).
  // A refusal is `BBF === 0`.
  //
  // ⚠️ null means "could not tell" (slow reply, no board): keep the card at its own
  // odds rather than hiding it — a timeout must not empty the deck.
  const bbPriceCache = new Map<string, { at: number; factor: number | null }>();
  const BB_PRICE_TTL_MS = 45_000; // one poll cycle; odds move

  // ⭐ THE PRE-MATCH COMBO SPACE (bb-space.ts): a pre-match card is never priced on
  // the render path. It is shown only once a verdict is already in hand; otherwise
  // it is held back and queued for the off-render drain, which appears on the next
  // poll. See `priceBetBuilderCards` below and the drain kicked off after render.
  const bbSpace = new BbSpace();
  const bbQueue: Suggestion[] = [];
  // Fixtures whose drain has already bought one extra poll this page view.
  const bbRepolled = new Set<string>();
  // Fixtures whose sub-event board warm has already bought one extra poll this page view.
  const subLinesWarmTriedAt = new Map<string, number>();
  const subLinesWarmed = new Set<string>();
  // …and the same bound for the open fixture's ladder (see `ladderLanded`).
  const ladderRepolled = new Set<string>();
  const bbKey = (s: Suggestion): string =>
    outcomeKey(Number(s.legs![0].event_id), s.legs as never);
  async function bbPriceOne(s: Suggestion): Promise<SpaceEntry | null> {
    const legs = (s.legs ?? []).map((l) => Number(l.stake_id)).filter((n) => Number.isFinite(n) && n > 0);
    const eventId = Number(s.legs![0].event_id);
    const trace: PriceTrace = { source: "none", before: null, unchanged: null };
    const factor = trustedFactor(await priceViaService(eventId, false, legs, undefined, trace), trace)
      ?? (await catalog.betBuilderFactor(eventId, false, legs));
    if (factor == null) return null;
    const best = Math.max(...(s.legs ?? []).map((l) => Number(l.odds) || 0));
    const pair = legs.length >= 3 ? await bbPairFactor(eventId, false, legs) : null;
    return { at: Date.now(), factor, verdict: bbVerdict(factor, best, pair, legs.length) ?? "ok" };
  }
  const bbEmitVerdict = (s: Suggestion, _k: string, e: SpaceEntry): void => {
    client.sendEvent("", "bb_verdict", s.id ?? "", null, {
      event_id: String(s.legs![0].event_id),
      verdict: e.verdict,
      factor: e.factor,
      multiplied: combinedFactor((s.legs ?? []).map((l) => Number(l.odds) || 1)),
      // ⛔ `part` TRAVELS WITH THE LEG. `bb_verdicts.legs_key` keys on it, so a
      // verdict emitted without it is stored under a DIFFERENT key from the
      // suggestion it describes — and every later reader misses it silently.
      // The key and this payload move together or neither is worth anything.
      legs: (s.legs ?? []).map((l) => {
        const r = l as unknown as Record<string, unknown>;
        return {
          market_id: r.market_id ?? null, code: r.code ?? null,
          line: r.line ?? null, stake_id: l.stake_id,
          ...(r.part ? { part: r.part } : {}),
        };
      }),
    });
  };

  // The operator's best price among EVERY pair inside a triple. A triple has to
  // beat all of them, not just its best single leg — see `bbVerdict` and
  // `bestSubPairFactor`. Each pair shares `bbPriceCache` with the full-card price,
  // under a `:sub:` key, so a pair already priced as its own card costs no call.
  async function bbPairFactor(eventId: number, isLive: boolean,
                              legs: number[]): Promise<number | null> {
    return bestSubPairFactor(legs, async (pair) => {
      const key = eventId + ":sub:" + pair.slice().sort((a, b) => a - b).join(",");
      const hit = bbPriceCache.get(key);
      if (hit && !isLive && Date.now() - hit.at <= BB_PRICE_TTL_MS) return hit.factor;
      const t: PriceTrace = { source: "none", before: null, unchanged: null };
      const factor = trustedFactor(await priceViaService(eventId, isLive, pair, undefined, t), t)
        ?? (await catalog.betBuilderFactor(eventId, isLive, pair));
      bbPriceCache.set(key, { at: Date.now(), factor });
      return factor;
    });
  }

  async function priceBetBuilderCards(list: Suggestion[]): Promise<void> {
    const cards = list.filter(isBetBuilder);
    if (!cards.length) return;
    const now = Date.now();
    for (const s of cards) {
      const legs = (s.legs ?? []).map((l) => Number(l.stake_id)).filter((n) => Number.isFinite(n) && n > 0);
      if (legs.length < 2) continue;
      const eventId = Number(s.legs![0].event_id);
      if (!Number.isFinite(eventId) || eventId <= 0) continue;
      // ⭐ PRE-MATCH: never priced here. Show only a verdict already in hand;
      // queue the rest for the off-render pass (see bb-space.ts).
      // ⭐ The book's own `ok` for this combo, from whichever browser paid for
      // it (`bb_verdicts.fresh_ok`). Seeded before the decision so an accepted
      // combo renders on the FIRST poll instead of waiting for this tab to
      // re-price it — see `seedServed`.
      const bbk = bbKey(s);
      seedServed(s, bbk, bbSpace);
      const pre = prematchDecision(s, bbk, bbSpace);
      if (pre === "queue") { bbQueue.push(s); (s as unknown as Record<string, unknown>).bb_hold = true; continue; }
      if (pre === "hold") { (s as unknown as Record<string, unknown>).bb_hold = true; continue; }
      if (pre !== "live") {
        (s as unknown as Record<string, unknown>).bb_factor = pre.show;
        s.combined_odds = pre.show;
        continue;
      }
      const key = eventId + ":" + legs.slice().sort((a, b) => a - b).join(",");
      let hit = bbPriceCache.get(key);
      if (!hit || s.is_live === true || now - hit.at > BB_PRICE_TTL_MS) {
        // ⛔ THE APP'S OWN CALL FIRST. Our replay needs a readable JWT and silently
        // 401s when the app has not hydrated one — measured on a live tap: the card
        // then showed its multiplied product and the value check had nothing to
        // refuse on, so a pair the book prices at 1.73 was added under its own 1.93
        // leg. The service call is authenticated by construction.
        const trace: PriceTrace = { source: "none", before: null, unchanged: null };
        let factor = trustedFactor(
          await priceViaService(eventId, s.is_live === true, legs, undefined, trace), trace);
        if (factor == null) {
          factor = await catalog.betBuilderFactor(eventId, s.is_live === true, legs);
          trace.source = factor == null ? "none" : "replay";
        }
        (s as unknown as Record<string, unknown>).bb_src = trace.source;
        (s as unknown as Record<string, unknown>).bb_store_unchanged = trace.unchanged;
        (s as unknown as Record<string, unknown>).bb_store_before = trace.before;
        hit = { at: Date.now(), factor };
        bbPriceCache.set(key, hit);
      }
      const best = Math.max(...(s.legs ?? []).map((l) => Number(l.odds) || 0));
      if (hit.factor == null) {
        // ⛔ LIVE SAME-MATCH ACCAS FAIL CLOSED. A null here means the operator's
        // current Bet Builder price was not confirmed. Keeping the suggestion
        // would let its old `combined_odds` survive after the book disabled BB
        // for this fixture (the Bayern–Union 7–0 case). Pre-match cards are held
        // off the render path; live cards must be removed, not served stale.
        (s as unknown as Record<string, unknown>).bb_drop = "unpriced";
        continue;
      }
      (s as unknown as Record<string, unknown>).bb_factor = hit.factor;
      // ⛔ A THREE-LEG CARD CAN BE A DISGUISED TWO-LEG CARD. Beating the best single
      // leg is not enough: the book pays nothing for a leg the others already imply
      // (П1 + Обе-Да pays 4.15, and adding Тотал>2.5 pays **4.15** too). So a triple
      // is priced a second time against its own first two legs. One extra call, on
      // 3-leg cards only, sharing the 45s cache.
      const pair = legs.length >= 3
        ? await bbPairFactor(eventId, s.is_live === true, legs) : null;
      const why = bbVerdict(hit.factor, best, pair, legs.length);
      if (why) {
        (s as unknown as Record<string, unknown>).bb_drop = why;
        continue;
      }
      s.combined_odds = hit.factor;                            // the OPERATOR's price
    }
    // Held cards (pre-match, unpriced or not yet `ok`) leave the render silently —
    // their verdict, once known, is reported once by the emit in the drain (Task 3).
    const dropped = settleRender(list, cards);
    if (dropped.length) {
      client.sendEvent("", "betslip_add", dropped[0].id ?? "", null, {
        add_verdict: "bet-builder-dropped-preview",
        dropped: dropped.map((s) => ({
          event_id: s.event_id,
          reason: (s as unknown as Record<string, unknown>).bb_drop,
          bb_factor: (s as unknown as Record<string, unknown>).bb_factor,
          multiplied: combinedFactor((s.legs ?? []).map((l) => Number(l.odds) || 1)),
          legs: (s.legs ?? []).length,
          // ⛔ WHICH PRICE SOURCE ANSWERED, and whether the app's store already
          // held that number before we asked. `betBuilderData.BBF` is ONE unkeyed
          // slot, so a `0` can be the book refusing this pair OR a leftover from
          // the previous card / an empty selection. MEASURED 2026-09-14: 829 of
          // the refused cards sit on 161 fixtures where the SAME pair shapes price
          // fine elsewhere (П1 + Тотал>2.5 → 3.05), so the two cannot be told
          // apart without this field.
          price_source: (s as unknown as Record<string, unknown>).bb_src ?? null,
          store_unchanged: (s as unknown as Record<string, unknown>).bb_store_unchanged ?? null,
          store_before: (s as unknown as Record<string, unknown>).bb_store_before ?? null,
          // ⛔ WHICH STRATEGY WAS IN THE COMBO — the whole point of this event.
          // Without it a refusal is a COUNT (1,926/day, measured 2026-09-13) and
          // nothing can name a market pair, so `tier_lines._combinable` keeps
          // proposing combinations only the OPERATOR knows it will refuse and the
          // card dies in the browser. The operator's answer is only obtainable
          // client-side (the backend has no session with it), so this payload IS
          // the only channel that can turn the refusals into a rule.
          leg_markets: (s.legs ?? []).map((l) => ({
            market_id: (l as unknown as Record<string, unknown>).market_id ?? null,
            code: (l as unknown as Record<string, unknown>).code ?? null,
            line: (l as unknown as Record<string, unknown>).line ?? null,
            market_name: (l as unknown as Record<string, unknown>).market_name ?? null,
          })),
        })),
      });
    }
  }

  // The on-screen diagnostic (`?bcdiag=1`). Off by default and costs nothing when
  // off. It exists because the only open question on this surface — does the gate
  // fail to RUN, or does the DOM lie to it? — can only be answered on a phone,
  // where there is no console and the chip's own data is not a liveness tell.
  // Re-price every card from the live board on each render. The suggestion's own
  // odds are a snapshot of the /suggestions response and nothing refreshed them —
  // measured 2026-08-05: a card offering 1.33 over a board holding 1.02, added
  // silently at the new price. Only "moved" and "frozen" reach the UI; "gone" and
  // "no-event" deliberately do not, because a stake missing from OUR pool is still
  // bettable at the book (same reading: a leg absent here landed and traded).
  // Tapping the club names opens that match — the operator's own betslip gesture.
  // Rendered as a link ONLY where a navigation route is reachable; elsewhere the
  // row stays plain text rather than promising something it cannot do.
  renderer.canOpenMatch = () => (sink as { canOpenMatch?: () => boolean }).canOpenMatch?.() ?? false;
  const briefExposures = new BriefExposures();
  renderer.onBriefAction = briefActionHandler(briefExposures, (s, meta) => {
    client.sendEvent("", "brief_interaction", s.id, null, { event_id: s.event_id, ...meta });
  });
  renderer.onOpenMatch = (s, eventId) => {
    const sportId = typeof s.sport_id === "number" ? s.sport_id : null;
    const moved = (sink as {
      openMatch?: (e: string, sp: number | null, live: boolean) => boolean;
    }).openMatch?.(eventId, sportId, s.is_live !== false) ?? false;
    if (moved) {
      busWatcher.recordNavigate(s, Number(eventId));
      qualifiedSelfOpen.reacted();
    }
    // Only a navigation that REALLY happened becomes influence. This is the trigger
    // the `navigated` attribution was built for: from here the user picks their own
    // market on that match, and a bet on it is ours to have influenced.
    const exposure = renderer.exposureIdFor(s);
    client.sendEvent("", "open_match", s.id, null, withOutcomeDims({
      event_id: eventId,
      rec_type: s.rec_type,
      opened: moved,
      brief: briefExposures.forOutcome(s),
      // Save the rendered sentence only when navigation really landed. A failed
      // click is diagnostic telemetry, not evidence about advice copy.
      ...(moved ? { advice_snapshot: adviceSnapshot(s) } : {}),
      // The card's tier, so `by_dimension.tier.*.match_opens` stops reading 0
      // for the opens the tier experiment exists to measure.
      ...(openMatchDims(s, exposure) ? { dims: openMatchDims(s, exposure) } : {}),
    }, renderer.launcherVariant()));
    return moved;
  };

  // ★ save → the OPERATOR'S own favourites, so it shows up in their Фавориты,
  // survives a reload and follows the user to another device — none of which a
  // widget-local list could do. Only ever on an explicit tap; never as a side
  // effect of an impression or an add, because this writes to a real account.
  favourites.account = catalog as unknown as {
    canCallAccount(): boolean;
    callAccount(path: string, data: Record<string, unknown>): Promise<unknown>;
  };

  // Why the ★ is (not) on the card. `canStar()` is a boolean with four inputs and
  // none of them were reported anywhere — see star-gate.ts. Falls back to an
  // all-false reading rather than throwing on a source that has no gate to report.
  const readStarGate = (): StarGateRead => {
    try {
      return (catalog as unknown as { starGate?: () => StarGateRead }).starGate?.()
        ?? { cw: false, tpl: false, auth: false, jwt: false, can: false };
    } catch {
      return { cw: false, tpl: false, auth: false, jwt: false, can: false };
    }
  };
  renderer.canStar = () => favourites.canStar();
  renderer.isStarred = (eventId) => favourites.isStarred(Number(eventId));
  renderer.onToggleStar = async (s, eventId, want) => {
    const got = await favourites.setEventStarred(Number(eventId), want);
    qualifiedSelfOpen.reacted();
    const exposure = renderer.exposureIdFor(s);
    client.sendEvent("", "star_match", s.id, null, {
      event_id: eventId,
      rec_type: s.rec_type,
      want,
      // What the OPERATOR ended up holding. `want !== saved` is a silently-failed
      // write, which is the failure this endpoint is known to return 200 for.
      saved: got,
      ...(exposure ? { dims: { exposure_id: exposure } } : {}),
    });
    return got;
  };

  renderer.priceProbe = (eventId, stakeId, cardOdds) => {
    const st = catalog.stakeState(eventId, stakeId, cardOdds);
    return st.state === "moved" || st.state === "frozen"
      ? { state: st.state, odds: st.odds }
      : null;
  };

  renderer.matchProbe = (eventId) => {
    const ev = Number(eventId);
    if (!Number.isFinite(ev)) return null;
    const st = catalog.matchState(ev);
    return { score: st.score };
  };

  renderer.prematchProbe = (eid) =>
    (catalog.prematchBoard().find((m) => String(m.id) === eid) as { starts_at?: string } | undefined) ?? null;

  renderer.liveTab = () => isLiveTab(window);

  let diagPollTs: number | null = null;
  let diagPool = 0;
  let diagSugg = 0;
  let diagLastAdd = "";
  // The strip itself is started further down, once `skips` and `slipHold` exist —
  // both diag lines read them, and `startDiag` renders synchronously on the first
  // tick, so starting it here (before either is declared) would throw on the TDZ.

  // Launcher funnel: pill_impression (once, when the launcher is first on screen)
  // → pill_open (every click) → betslip_add. Until now the launcher emitted nothing,
  // so its open rate — the number that decides whether the overlay is even seen —
  // was unmeasurable. Not sent in collector mode: no viewer, no launcher.
  if (!collector) {
    // ⛔ THE LAUNCHER IS THE COMPACT PRESENTATION, and it says so rather than
    // leaving the reader to infer it from the event name. `expand_reason` is
    // absent here on purpose: nothing has expanded yet.
    renderer.onPillShown = (p) => {
      client.sendEvent("", "pill_impression", "", null,
        { ...p, device, locale, dims: { presentation: "compact" } });
    };
    renderer.onPillOpen = (p) => {
      client.sendEvent("", "pill_open", "", null,
        { ...p, device, locale, dims: { presentation: "compact" } });
    };
    // Distinct from `pill_open`: this tap did not open our UI at all, it sent the
    // user to their coupon. Counting it as a pill open would inflate the very
    // open-rate the launcher A/B reads.
    //
    // Fired UNCONDITIONALLY, `opened` carrying whatever betslipOpener() actually
    // returned — same shape as onOpenMatch's `opened: moved` a bit further down:
    // "so a navigation route that silently failed is visible instead of assumed".
    // A tap that finds no slip used to produce no DOM change AND no telemetry.
    renderer.onSlipHoldOpen = (opened, via) => {
      client.sendEvent("", "slip_hold_open", "", null, { opened, via: via ?? "betslip", device, locale });
    };
  }

  // Every OUTCOME event carries the launcher treatment that was on screen when it
  // happened — see outcomeDims() in event-dims.ts.
  const dimsOf = (s: Suggestion, rank: number) => ({
    ...outcomeDims(s, rank, renderer.launcherVariant(), renderer.exposureIdFor(s)),
  });
  // ⛔ Only an INFLUENCED placement carries dims. Stamping EVERY bet_placed put
  // the operator's whole book into the dimension cells — organic bets from
  // visitors who never saw a card — while the denominator stayed our own
  // impressions (5,613 organic against 144 attributed on the pilot). A bet we
  // had nothing to do with keeps `dims: undefined`.
  const dimsIfOurs = (meta: Record<string, unknown>) =>
    (meta.influenced || meta.attributed || Number(meta.shown_leg_count) > 0)
      ? withOutcomeDims(meta, renderer.launcherVariant())
      : meta;

  const ictx = { device, locale };
  const qualifiedSelfOpen = new QualifiedSelfOpenController(apiKey, renderer, operatorConfig.theme);
  qualifiedSelfOpen.onRefused = (reason, target) => {
    const s = currentSuggestions.find((x) => x.id === target.cardId);
    client.sendEvent("", "self_open_refused", target.cardId, null, {
      event_id: target.eventId, reason,
      ...(s ? { dims: suggestionDims(s, currentSuggestions.indexOf(s)) } : {}),
    });
  };

  // ⛔ NOT an `impression`: the pill is the compact surface, and an advice card
  // is never a compact impression. One row per delivered push (the controller
  // spends the trigger), so pushes stay countable after 9d836abb moved them
  // from the peek to the pill.
  qualifiedSelfOpen.onPushed = (target) => {
    const s = currentSuggestions.find((x) => x.id === target.cardId);
    client.sendEvent("", "pill_push", target.cardId, null, {
      event_id: target.eventId, ...ictx,
      dims: { ...(s ? suggestionDims(s, currentSuggestions.indexOf(s)) : {}),
              presentation: "compact", expand_reason: "signal" },
    });
  };

  // Same impression event whether the pick was seen in the full sheet or the
  // peek — same fields, same rank semantics — so a peek impression is
  // indistinguishable downstream from a sheet impression and the strategy
  // table / dimension slices don't split into two incompatible populations.
  // Shared with the sheet-poll loop below.
  /** The two presentation dims, read at SEND time so they describe the surface
   *  the event actually happened on. */
  const presentationDims = () => ({
    presentation: renderer.currentPresentation(),
    expand_reason: renderer.currentExpandReason(),
  });
  const diagnosticOnCurrent = (s: Suggestion): boolean | null => renderer.openMatchEventId == null ? null
    : String(s.event_id ?? "") === renderer.openMatchEventId;
  const captureFactDiagnostic = makeFactDiagnosticSender(client, diagnosticOnCurrent);
  const rawSendImpression = makeImpressionSender(
    client, dimsOf, () => renderer.currentPeekExposureId(), presentationDims,
    diagnosticOnCurrent, captureFactDiagnostic);
  // Armed on the FIRST card the user is shown — that is the moment the ★ either
  // was or was not offered, and reading the gate at boot would only ever say "not
  // yet". Sends one event per session, 30s later, carrying both readings.
  // Not in collector mode: no cards, no star, nobody to report for.
  // ⛔ Once per SESSION, not once per mount — the SPA remounts the SDK on every
  // navigation, so an instance flag would put a star_gate on the wire per page
  // view. Same sessionStorage pattern as the slip nudge above.
  const STAR_GATE_SESSION_KEY = `bc_star_gate:${apiKey}`;
  const starGateOnce = {
    done: () => { try { return !!sessionStorage.getItem(STAR_GATE_SESSION_KEY); } catch { return false; } },
    mark: () => { try { sessionStorage.setItem(STAR_GATE_SESSION_KEY, "1"); } catch { /* probe once per mount then */ } },
  };
  const starGateTimers = new Set<ReturnType<typeof setTimeout>>();
  const scheduleStarGate = (fn: () => void, ms: number): ReturnType<typeof setTimeout> => {
    const timer = setTimeout(() => { starGateTimers.delete(timer); if (!stopped) fn(); }, ms);
    starGateTimers.add(timer);
    return timer;
  };
  const probeStarGate = collector
    ? () => { /* no viewer */ }
    : makeStarGateProbe(client, readStarGate, STAR_GATE_DELAY_MS, scheduleStarGate, starGateOnce);
  const sendImpression = (sg: Suggestion, rank: number): void => {
    probeStarGate();
    rawSendImpression(sg, rank);
    // The outcome this card showed, for the `shown` claim. `busWatcher` is declared
    // further down; an impression before it exists is simply not filed.
    try { busWatcher.recordShown(sg, rank); } catch { /* not constructed yet */ }
  };


  // Mobile keeps the compact peek as the only normal-flow surface. Historical
  // `peek_all_picks` rows remain ingestible for older immutable bundles, but no
  // current callback emits the retired action.
  let timePeek = false, timePanel = false, timePagePaused = false;
  const readSportsbookTime = () => ({
    account: collector ? null : timeAccount(),
    visible: !timePagePaused && !document.hidden && document.hasFocus(),
    mounted: readHostGeometry(theme.sportsbook_root).sportsbookMounted === true,
    widget: !holdoutHidden && (timePeek || timePanel),
  });
  const sportsbookTime = makeSportsbookTime(readSportsbookTime, (account, sportsbook_ms, widget_ms, observation) => {
    void client.sendEvent("", "sportsbook_time", "", null,
      { user_hash: account, sportsbook_ms, widget_ms, ...observation });
  });
  client.setTimeStateReader(account => account === timeAccount() && !collector
    ? sportsbookTimeState(readSportsbookTime()) : null);
  observeTimeAccount = sportsbookTime.tick;
  beginExperimentTime = () => { sportsbookTime.tick(); sportsbookTime.flush(); };
  sportsbookTime.tick(); sportsbookTime.flush();
  experiment.tick();
  trackInterval(() => { experiment.tick(); sportsbookTime.tick(); }, SPORTSBOOK_TIME_SAMPLE_MS);
  trackInterval(() => experiment.tick(true), SPORTSBOOK_TIME_FLUSH_MS);
  trackInterval(() => { sportsbookTime.tick(); sportsbookTime.flush(); }, SPORTSBOOK_TIME_FLUSH_MS);
  const flushSportsbookTime = () => { sportsbookTime.tick(); sportsbookTime.flush(); };
  const holdout = !collector && operatorConfig.holdout_capable ? makeHoldout(
    ()=>timeAccount(), user=>client.fetchHoldout(user),
    off=>{holdoutHidden=off;renderer.setExperimentSuppressed(off);},
    flushSportsbookTime,
    ()=>{void client.sendEvent("","experiment_presence","",null);sportsbookTime.tick();sportsbookTime.flush();},
    ()=>readSportsbookTime().visible && readSportsbookTime().mounted,
  ) : null;
  if(holdout){
    client.setHoldoutReader(holdout.meta,holdout.suppressed);
    holdoutTick=(refresh=false)=>{void holdout.tick(refresh);};
    holdoutTick(true);
    trackInterval(()=>holdoutTick(true),SPORTSBOOK_TIME_FLUSH_MS);
    trackInterval(()=>holdoutTick(),SPORTSBOOK_TIME_SAMPLE_MS);
  }
  const pauseSportsbookTime = () => {
    flushSportsbookTime(); timePagePaused = true; sportsbookTime.tick(); sportsbookTime.flush();
  };
  const resumeSportsbookTime = () => {
    timePagePaused = false; sportsbookTime.tick(); experiment.tick();
  };
  document.addEventListener("freeze", pauseSportsbookTime);
  document.addEventListener("resume", resumeSportsbookTime);
  window.addEventListener("pageshow", resumeSportsbookTime);
  document.addEventListener("visibilitychange", flushSportsbookTime);
  window.addEventListener("blur", flushSportsbookTime);
  window.addEventListener("focus", sportsbookTime.tick);
  window.addEventListener("pagehide", pauseSportsbookTime);
  renderer.onPeekOpen = (reason, index) => {
    sportsbookTime.tick(); timePeek = true; sportsbookTime.tick();
    const s = renderer.peekSuggestionAt(index);
    const exposure = renderer.currentPeekExposureId();
    const cardDims = s ? suggestionDims(s, index) : {};
    client.sendEvent("", "peek_open", "", null,
      { ...peekOpenMeta(reason, index, ictx), dims: {
        ...cardDims, ...(exposure ? { exposure_id: exposure } : {}),
        ...presentationDims() } });
    // The peek shows a card the collapsed-panel guard below never sees — an
    // `impression` here is the ONLY way that card is ever counted as seen.
    if (s) sendImpression(s, index);
  };
  // The advice-rating card, dealt into the mobile deck until answered once.
  renderer.npsEnabled = !collector;
  renderer.onNps = (score, index) => {
    client.sendEvent("", "nps", "", null, { score, card_index: index + 1, ...ictx });
  };
  renderer.onPeekLanded = (index) => {
    const s = renderer.peekSuggestionAt(index);
    if (s) sendImpression(s, index);
  };
  renderer.onPeekClose = (reason, dwellMs, picksSeen) => {
    sportsbookTime.tick(); timePeek = false; sportsbookTime.tick();
    const exposure = renderer.currentPeekExposureId();
    const s = renderer.peekCurrentSuggestion();
    client.sendEvent("", "peek_close", "", null,
      // ⛔ The index the card is actually AT, never a hardcoded 0 — that made
      // the rank stamped on every card past the first a lie (0eeb34f).
      { ...peekCloseMeta(reason, dwellMs, picksSeen, ictx), dims: {
        ...(s ? suggestionDims(s, renderer.peekIndexOf(s)) : {}),
        ...(exposure ? { exposure_id: exposure } : {}) } });
    // The controller owns suppression outside the renderer instance, so remounts
    // cannot reopen the same qualified trigger. Deliberate reactions clear it.
    qualifiedSelfOpen.closed(reason);
  };
  // The full sheet's own dwell. Together with `peek_close.dwell_ms` this is the
  // total time the widget actually held the screen — the number the operator
  // dashboard sums into `engaged_ms`. Both clocks pause on a hidden tab.
  const sendOnScreenImpressions = (): void => {
    const onScreen = renderer.visibleSheetIds();
    currentSuggestions.forEach((s, rank) => { if (onScreen.has(s.id)) sendImpression(s, rank); });
  };
  renderer.onSheetScroll = sendOnScreenImpressions;
  renderer.onPanelOpen = () => {
    sportsbookTime.tick(); timePanel = true; sportsbookTime.tick();
    client.sendEvent("", "panel_open", "", null, { ...ictx });
    sendOnScreenImpressions();
  };
  renderer.onPanelClose = (engagedMs) => {
    sportsbookTime.tick(); timePanel = false; sportsbookTime.tick();
    client.sendEvent("", "panel_close", "", null, panelCloseMeta(engagedMs, ictx));
  };
  // Capped per session: a user flicking through picks must not be able to spend
  // the whole 60/min event budget on navigation and lose their own placement.
  // The cap governs the `peek_nav` ANALYTICS event only — it must never also
  // suppress the impression, or a user who navigates past it could add a pick
  // that's never reported as seen (adds exceeding impressions all over again).
  let navSent = 0;
  const PEEK_NAV_CAP = 20;
  renderer.onPeekNav = (method, direction, toIndex, fromIndex) => {
    if (navSent < PEEK_NAV_CAP) {
      navSent += 1;
      client.sendEvent("", "peek_nav", "", null,
        peekNavMeta(method, direction, toIndex, fromIndex, ictx,
          renderer.currentPeekExposureId()
            ? { exposure_id: renderer.currentPeekExposureId()! } : undefined));
    }
    // Navigating puts a DIFFERENT card in front of the user — that's a new
    // impression, same as the open above (dedup still holds via sendImpression).
    const s = renderer.peekSuggestionAt(toIndex);
    if (s) sendImpression(s, toIndex);
  };
  renderer.onPillDrag = () => {
    client.sendEvent("", "pill_drag", "", null, ictx);
  };
  // The user got rid of the widget. `reason` separates the three gestures: a
  // header × decided with the picks in view, a pill × on something never opened,
  // and a drag off the edge — the only one that can be an accident. The renderer
  // has already torn itself down; persisting the choice here is what stops the
  // next poll, and the next page in this session, from bringing it back.
  renderer.onHide = (reason) => {
    sportsbookTime.tick(); timePeek = false; timePanel = false; sportsbookTime.tick();
    markHidden(apiKey);
    client.sendEvent("", "widget_hide", "", null, { reason, ...ictx });
  };
  renderer.onGateChange = (visible) => {
    client.sendEvent("", "gate_change", "", null, { visible, ...ictx });
  };

  // Placement attribution. TWO sources, because the operators differ:
  //
  //  • BettingBusWatcher (preferred) reads Digitain's own /Betting/* traffic off
  //    the relay stream we already listen to — a real client-side receipt with an
  //    order number, the stake amount, and every leg at its ACCEPTED price. It
  //    classifies the placed coupon compositionally (ours_exact / ours_partial /
  //    organic) instead of crediting anything that happens to land in a window.
  //  • BetPlacedWatcher (legacy) is the contentless `successBet` pulse from the
  //    sportsbook iframe. It does NOT fire on Parikara (there is no such iframe).
  //
  // Both start; the FIRST /Betting/ message retires the legacy one for the rest of
  // the session. Feature detection rather than per-operator config, so an operator
  // without the bus keeps working untouched and one with it upgrades silently.
  const legacyWatcher = new BetPlacedWatcher((a) => {
    // The placement consumed the slip — let the sink re-post a future re-add.
    sink.clearAdded();
    const latest = a.adds.length ? a.adds[a.adds.length - 1] : null;
    client.sendEvent("", "bet_placed", latest ? latest.suggestion_id : "", null,
      dimsIfOurs({
        event_id: latest?.event_id,
        rec_type: latest?.rec_type,
        attributed: a.attributed,
        seconds_since_add: a.seconds_since_add,
        adds: a.adds,
        // Dims of the placed pick — an attributed placement is the strongest
        // positive signal per sport/band/type for tuning.
        dims: latest?.dims,
      }));
  });

  const busWatcher = new BettingBusWatcher(
    (p) => {
      sink.clearAdded();
      // The coupon did its job: hand the pill back to the next advice and let
      // the pending nudge die unfired (owner's rule, 2026-09-01).
      slipHold.placed();
      client.sendEvent("", "bet_placed", p.suggestion_id ?? "", null,
        dimsIfOurs(placementEvent(p)));
    },
    () => legacyWatcher.stop(), // the bus exists here — retire the pulse watcher
    // The board we already hold, keyed by stake id — so a leg the user found on
    // their own becomes readable without a single extra request.
    (stakeId) => catalog.stakeFacts(stakeId),
    (r) => {
      client.sendEvent("", "bet_rejected", "", null, { ...r, ...ictx });
    },
    // BOTH boards, by event id — this is what lets a placed leg say whether the
    // match had started. Without it every leg we could name was live, because
    // the live catalogue was the only thing a leg could resolve against.
    (eventId) => catalog.eventFacts(eventId),
  );
  busWatcher.setSubEventParts((eventId) => catalog.partOfSubEvent(eventId));
  // ⛔ A SECOND reading of the coupon, independent of the bus. Measured on the box
  // 2026-09-12: every add for ~5 hours came back `landed=0 / ack=timeout` — no
  // betting message arrived at all — while the app itself kept working and placing
  // bets. `couponStakeIds`/`landedStakes`/`confirmAdd` now also read the app's OWN
  // redux coupon (`common.betslip.CheckedStakesList`, verified in Digitain's
  // 8.0.26253.3 bundle), which on a native operator is in OUR document. Null when the
  // store is absent or reshaped — the bus answer then stands alone, as before.
  busWatcher.storeCoupon = () => storeCouponStakeIds(window);
  busWatcher.subscribeCoupon = (notify) => subscribeStoreCoupon(window, notify);
  // ⭐ The visit rule: what we inserted, showed and opened survives the SPA's SDK
  // remounts and a reload, for the life of the tab — not a 30-minute clock.
  try { busWatcher.persistTo(window.sessionStorage, `bc_bus_registry:${apiKey}`); } catch { /* memory only */ }
  // Owner 2026-10-12: "add favourites flag on legs too" — `fav` on every placed leg.
  busWatcher.setFavourites(() => favourites.snapshot());

  // Owner 2026-10-12: “we have no record of which list” — capture the operator's
  // own odds taps before their handler mutates the coupon, never touching the tap.
  let tapRoot: ShadowRoot | null = null;
  const onSportsbookTap = (event: Event): void => {
    try {
      const target = event.composedPath().find((node): node is Element => node instanceof Element);
      if (!target || !tapRoot) return;
      const entry = tapEntryFrom(target, tapRoot);
      if (!entry) return;
      busWatcher.noteTap(entry);
      void client.sendEvent("", "odds_tap", "", null,
        { ...entry, event_id: entry.event_id ?? undefined });
    } catch { /* a capture listener must never throw into the operator page */ }
  };
  const sportsbookRoot = (): ShadowRoot | null => {
    const host = document.querySelector("sport-mobile");
    return (host && (host as Element & { shadowRoot?: ShadowRoot | null }).shadowRoot) || null;
  };
  const ensureSportsbookSource = (): ShadowRoot | null => {
    const root = sportsbookRoot();
    if (root !== tapRoot) {
      if (tapRoot) tapRoot.removeEventListener("pointerdown", onSportsbookTap, true);
      tapRoot = root;
      if (tapRoot) tapRoot.addEventListener("pointerdown", onSportsbookTap, true);
    }
    if (!root) return null;
    try {
      const sessionId = client.getSessionId();
      if (session?.getItem("bc_session_entry_sent") !== sessionId) {
        let referrerHost = "";
        try { referrerHost = document.referrer ? new URL(document.referrer).host : ""; } catch { /* malformed referrer */ }
        const params = new URLSearchParams(location.search);
        void client.sendEvent("", "session_entry", "", null, {
          referrer_host: referrerHost,
          utm_source: (params.get("utm_source") ?? "").slice(0, 64),
          utm_medium: (params.get("utm_medium") ?? "").slice(0, 64),
          utm_campaign: (params.get("utm_campaign") ?? "").slice(0, 64),
          landing_path: location.pathname,
          landing_route: readRouterLocation(root)?.path ?? null,
        });
        session?.setItem("bc_session_entry_sent", sessionId);
      }
    } catch { /* arrival source is best-effort board context */ }
    return root;
  };
  ensureSportsbookSource();

  // ── The finish-the-bet layer (owner-approved 2026-09-01) ──────────────────
  // Confirmed adds were dying between coupon and placement (39% of adding
  // sessions place nothing; traced live on Амед СК and Villa–Arsenal). Three
  // owner-scoped moves, all inside OUR widget — never their UI:
  //  1. betslip_remove telemetry — the forgetter/rejecter split;
  //  2. the pill HOLDS the unplaced pick with its live coupon price;
  //  3. one nudge per session if it sits 2 minutes unplaced.
  // A placement clears everything and the pill returns to the next advice.
  const NUDGE_SESSION_KEY = `bc_slip_nudge:${apiKey}`;
  // ⛔ WHEN the hold started, surviving the SPA's constant rebuilds of the SDK.
  // The one-per-session flag below stops a SECOND nudge and always worked; this
  // is what makes the FIRST one fire at all. Without it the two-minute countdown
  // restarted on every navigation and a normally-active user never reached it.
  const HELD_SINCE_KEY = `bc_slip_held_since:${apiKey}`;
  const heldSince = {
    read: () => {
      try {
        const v = Number(sessionStorage.getItem(HELD_SINCE_KEY));
        return Number.isFinite(v) && v > 0 ? v : null;
      } catch { return null; }
    },
    write: (ts: number) => { try { sessionStorage.setItem(HELD_SINCE_KEY, String(ts)); } catch { /* per-mount then */ } },
    clear: () => { try { sessionStorage.removeItem(HELD_SINCE_KEY); } catch { /* nothing held anyway */ } },
  };
  // ⛔ Read at FIRE time, not once at boot: a widget instance the SPA discarded
  // still owns a live setTimeout, and a boolean captured at construction let it
  // nudge a session another instance had already nudged.
  const nudgeAllowed = () => {
    try { return !sessionStorage.getItem(NUDGE_SESSION_KEY); } catch { return true; }
  };
  let lastHold: { id?: string; card?: unknown; label: string; odds: number | null; count: number } | null = null;
  const slipHold = new SlipHoldTracker(
    (h) => {
      lastHold = h;
      renderer.setSlipHold(h);
    },
    () => {
      try { sessionStorage.setItem(NUDGE_SESSION_KEY, "1"); } catch { /* still show it */ }
      const text = lastHold && lastHold.odds != null
        ? t(locale, "slip.nudge", { odds: lastHold.odds })
        : t(locale, "slip.nudge_plain");
      renderer.showSlipNudge(text);
      client.sendEvent("", "slip_nudge", "", null, {
        odds: lastHold ? lastHold.odds : null, locale,
      });
    },
    120_000,
    nudgeAllowed,
    heldSince,
  );
  busWatcher.onOurLegRemoved = (r) => {
    // The user dropped a confirmed pick of ours without betting it. held_ms is
    // the number the nudge timing should be tuned from.
    client.sendEvent("", "betslip_remove", "", null, {
      event_id: r.eventId != null ? String(r.eventId) : undefined,
      stake_id: r.stakeId,
      held_ms: r.heldMs,
      locale,
    });
    slipHold.removeStake(r.stakeId);
  };
  // The held pick's price is the operator's own coupon factor, re-read on a
  // slow tick — refresh() no-ops with nothing held, so this costs nothing then.
  // ⛔ A coupon that reads EMPTY twice running (10s) releases the hold even
  // when no bus message said so. Probe 2026-09-10 02:27 UTC: «Билет пуст» on
  // screen, `betslip_remove` never fired, the pill still read «✓ Комо · 1.83».
  // Two reads, not one: the disc reads empty for a beat while Digitain
  // re-adds a toggled leg. Only the collapsed disc carries the count; an
  // expanded coupon reads null and is left alone.
  let emptyCouponTicks = 0;
  trackInterval(() => {
    if (!collector && document.visibilityState !== "hidden") {
      const visible = renderer.visiblePeekSuggestion();
      if (visible) captureFactDiagnostic(visible.suggestion);
      const sheet = renderer.visibleSheetIds();
      currentSuggestions.forEach((s) => { if (sheet.has(s.id)) captureFactDiagnostic(s); });
    }
    try {
      if (slipHold.active()) {
        const g = readHostGeometry(theme.sportsbook_root);
        emptyCouponTicks = (g.probeOk && g.couponLegs === 0) ? emptyCouponTicks + 1 : 0;
        if (emptyCouponTicks >= 2) { emptyCouponTicks = 0; slipHold.expireGone(() => false); }
      } else {
        emptyCouponTicks = 0;
      }
    } catch { /* a probe failure never touches the hold */ }
    // A combo is re-multiplied from its legs' coupon factors; the coupon's own
    // total wins when the operator shows one AND the coupon holds only our legs
    // (a stranger's leg would fold into that number).
    slipHold.refresh(
      (id) => busWatcher.acceptedOdds(id),
      (legs) => (busWatcher.slipSize() === legs ? readCouponTotal() : null),
    );
    // ⛔ Only when BOTH boards are populated — most sessions never pull the
    // pre-match board (prematch_pull_share) and the pull is async, so an
    // empty board must read as "unknown", never as "every event ended".
    const live = catalog.getCandidates();
    const pre = typeof catalog.prematchBoard === "function" ? catalog.prematchBoard() : [];
    if (!boardsKnown(live.length, pre.length)) return;
    const known = new Set([...live, ...pre].map((m) => String((m as { id?: unknown }).id ?? "")));
    slipHold.expireGone((eid) => known.has(eid));
  }, 5_000);

  // No user in collector mode → no placements to attribute; don't watch.
  if (!collector) {
    legacyWatcher.start();
    busWatcher.start();
  }

  // In-session browse history (distinct sport/league viewed) for personalization.
  // The bus reports names in the USER'S language; join the feed pool on event id
  // (same trick as current_context) to also carry the always-English copy — the
  // backend's league/sport matching keys on it.
  const browseHistory: Array<{
    sport: string; league: string; sport_en?: string; league_en?: string; event_id?: string;
  }> = [];
  const recordBrowse = (): void => {
    const m = source.getMatches()[0];
    if (!m || !m.league) return;
    // ⭐ DEDUPE ON THE EVENT, not on (league, sport). Browsing five La Liga
    // matches used to collapse into ONE entry, so the history could say "this
    // person likes La Liga" and could never say WHICH match they are looking at
    // — the one fact the ranker most needs. The event id is what makes the open
    // fixture rankable (see `ranking._personalization_sets`).
    const eventId = m.id ? String(m.id) : "";
    const seen = eventId
      ? browseHistory.some((b) => b.event_id === eventId)
      : browseHistory.some((b) => b.league === m.league && b.sport === m.sport);
    if (seen) return;
    const poolMatch = catalog.getCandidates().find((p) => p.id === m.id);
    browseHistory.push({
      sport: m.sport,
      league: m.league,
      sport_en: poolMatch?.sport_en ?? "",
      league_en: poolMatch?.league_en ?? "",
      event_id: eventId || undefined,
    });
    // The window is now per MATCH rather than per league, so it fills faster.
    // Keep the most recent — the match ON SCREEN must never be evicted by a
    // league the user left ten fixtures ago.
    if (browseHistory.length > 20) browseHistory.shift();
  };

  // "Add to Betslip" → the real one-tap add: toggle the pick's stake(s) into the
  // operator betslip BY STAKE ID via the cross-origin app-event (live-proven
  // 2026-07-07 — no booking number needed). One post per combo leg.
  //
  // Truthfulness gate — a Digitain stake id names a PRICE, not an outcome, so a
  // toggle for a stale stake does not do what the card then claims.
  //
  // Rotation is NOT driven by price movement: measured on Parikara 2026-08-13, one
  // match's 1X2 ids held for 50 minutes across halftime and a 1.13→1.05 / 16.5→35
  // drift. What rotates them is the market being taken DOWN and re-opened — and
  // then the old id is not merely stale, it is poison: Digitain accepts it into the
  // coupon and flags it `Deleted`, leaving a greyed row that "Принять" cannot fix.
  // See the REPAIR block below, which swaps such a leg onto the board's current id.
  // Two strategies, picked by what the operator actually answers:
  //
  //  • ACK (Parikara): post the toggles, then read the operator's own
  //    `/Betting/AddStake` — it says whether each leg landed AND at what price.
  //    Direct evidence, and one fewer round-trip than the mint below.
  //  • MINT (Hannibalbet): the sportsbook is a cross-origin iframe that sends the
  //    parent no readable ack, so we mint a BookAbet for the exact legs as a proxy
  //    — StatusCode 0 means the stakes are live and the toggle will land.
  //
  // We post first and confirm after, so a *partially* landed combo is swept back
  // out — otherwise the user holds an acca they never asked for. The sweep takes
  // ONLY the legs `busWatcher.landedStakes()` says the operator's coupon really
  // holds: `toggleStake` ADDS a leg that is absent, so retracting an unlanded leg
  // inserts a bet the user never chose. That was live for a day (2026-08-03,
  // owner-reported) and the sweep was disabled outright until the sink could tell
  // posted from confirmed. It can now, so the sweep is back — correctly scoped.
  // `confirmAdd` still distinguishes "the operator said no" from "this operator
  // never answers"; only the latter falls through to the mint, so Hannibalbet
  // pays exactly the same one call it always did.
  /** Suggestion ids with an add resolution currently in flight. Distinct from
   *  the poll-loop's own `inFlight` flag further down this file. */
  const addInFlight = new Set<string>();

  /** What the held pick is called on the chip. DATA, not prose: the pick's own
   *  localized selection, else the fixture — nothing is invented, so nothing
   *  needs a native read. */
  /** Per-leg names for the hold, keyed by stake id, and the count label a
   *  shrinking combo falls back to. */
  const holdLegs = (s: Suggestion): Pick<Parameters<typeof slipHold.hold>[0], "legLabels" | "comboLabel"> => {
    const legs = (s.legs ?? []) as Array<{ stake_id?: unknown; selection?: unknown }>;
    const legLabels: Record<number, string> = {};
    for (const l of legs) {
      const id = Number(l.stake_id);
      const name = String(l.selection ?? "").trim();
      if (Number.isFinite(id) && id > 0 && name) legLabels[id] = name;
    }
    return { legLabels, comboLabel: (n: number) => t(locale, "combo.selection", { n }) };
  };
  const holdLabel = (s: Suggestion): string => {
    const sel = (s.selection || "").trim();
    if (sel) return sel;
    const home = String((s as unknown as Record<string, unknown>).home ?? "").trim();
    const away = String((s as unknown as Record<string, unknown>).away ?? "").trim();
    if (home && away) return `${home}–${away}`;
    return "✓";
  };

  renderer.onSuggestionClick = async (id: string, shown?: Suggestion): Promise<AddOutcome> => {
    const rank = currentSuggestions.findIndex((x) => x.id === id);
    // THE CARD THE USER TAPPED WINS over a lookup in a list that has moved on.
    // `currentSuggestions` is replaced by every poll (2.5-5s) while the card in
    // front of the user holds still — the peek is not re-rendered while open, on
    // purpose. A combo's id is a hash of its leg set, so it goes stale as soon as
    // the board does, and the lookup then returned nothing: the add died as
    // `no-suggestion` on an acca the user was looking straight at (owner,
    // 2026-08-05). `rank` stays best-effort — it is telemetry, not the add.
    const s = (shown?.brief ? shown : (rank >= 0 ? currentSuggestions[rank] : undefined)) ?? shown;
    if (!s) {
      // Every exit from this handler states itself on the strip. A tap that leaves
      // the previous add's line standing reads as "the add did nothing", and the
      // owner then screenshots a verdict belonging to a different pick entirely
      // (2026-08-05: a 3-leg acca showing `confirmed posted=1` from an earlier
      // single). A stale instrument is worse than none.
      diagLastAdd = `no-suggestion id=${id}`;
      return { added: false, navigated: false };
    }
    if (s.type === "fact") return { added: false, navigated: false };
    // Freeze the shown evidence before any async add or poll can replace the card.
    const briefAtTap = briefExposures.forOutcome(shown ?? s);

    // ⛔ THE BET-BUILDER FORK — a same-match parlay is placed via `/Betting/PlaceBet`
    // (the native bet-builder endpoint), NOT `toggleStake`, which Digitain's standard
    // slip refuses for a same-event combo. This is the ONLY path that PLACES a real
    // bet from the widget, so it gates on an explicit confirm + stake amount and
    // never fires from render. The multi-match acca (different events) keeps the
    // toggleStake path below.
    if (isBetBuilder(s)) {
      // ⛔ THIS PATH'S FAILURES WERE INVISIBLE OFF-DEVICE. The early exits below only
      // wrote to the on-screen diag strip, so `companion_events` holds 5,000+ adds
      // and ZERO `bet-builder*` rows (checked 2026-09-12): whether a same-match acca
      // works was unanswerable from the box, only from a screenshot. Every exit now
      // reports itself, with the legs we WANTED and how many matched.
      const reportBuilder = (stage: string, extra: Record<string, unknown> = {}): void => {
        // ⚠️ The operator's own gate travels with every verdict. For a hydrated
        // logged-in user it reads `enabled:true, live:true` (measured 2026-09-12);
        // read before the app finishes hydrating the SAME page answers
        // `enabled:false, userId:0`. We report rather than gate precisely because
        // those two are indistinguishable here, and gating on the early read would
        // hide the feature from anyone who taps fast.
        const raw = catalog.betBuilderGate ? catalog.betBuilderGate() : null;
        // ⛔ The operator's account id NEVER goes on the wire — it did until
        // 2026-09-15 (`bb_gate.userId`). Only whether there is one.
        const gate = raw ? (({ userId, ...rest }) => ({
          ...rest, logged_in: userId === null ? null : userId > 0,
        }))(raw) : null;
        client.sendEvent("", "betslip_add", id, null, {
      brief: briefAtTap,
          event_id: s.event_id, rec_type: s.rec_type, booked: false,
          add_verdict: `bet-builder-${stage}`,
          legs: eventLegs(s.legs),
          ...(gate ? { bb_gate: gate } : {}),
          dims: dimsOf(s, rank), ...extra,
        });
      };
      const eventId = Number(s.legs![0].event_id);
      diagLastAdd = `bet-builder event=${eventId} legs=${s.legs!.length}`;
      if (!Number.isFinite(eventId) || eventId <= 0) {
        diagLastAdd = "bet-builder:no-event";
        reportBuilder("no-event");
        return { added: false, navigated: false };
      }
      const board = await catalog.fetchBetBuilderStakes(eventId, s.is_live === true);
      if (!board) {
        // ⛔ "no-board" used to mean four different things. It means one of them at
        // a time now: the reply's own HTTP status, measured. 401 with authed=false
        // is the operator refusing an anonymous call (Parikara, 2026-09-12).
        const why = catalog.betBuilderFailure
          ? catalog.betBuilderFailure()
          : { status: null, authed: false, events: null };
        // ⛔ Four outcomes, four words. `no-book` is the COMMON one and it is not a
        // fault: measured 2026-09-12, four of six fixtures answered 200 with
        // `{"EV":[]}` — that operator simply prices no bet-builder book on them.
        // Reading it as `no-board` would count a healthy answer as a failure.
        const stage = why.status === 401 || why.status === 403
          ? "unauthed"
          : why.status == null ? "no-reply"
          : why.events === 0 ? "no-book"
          : "no-board";
        diagLastAdd = `bet-builder:${stage}${why.status == null ? "" : " http=" + why.status}`;
        reportBuilder(stage, {
          legs_matched: 0, http_status: why.status, authed: why.authed, board_events: why.events,
        });
        return { added: false, navigated: false };
      }
      const bblegs = buildBetBuilderLegs(s, board);
      if (bblegs.length < 2) {
        diagLastAdd = `bet-builder:${bblegs.length}/2 legs`;
        // The one number that separates "the board never came" from "the board came
        // and none of our legs are on it" — the unverified stake-pool split.
        reportBuilder("no-legs", { legs_matched: bblegs.length, board_stakes: board.stakes.length });
        return { added: false, navigated: false };
      }
      // ⛔⛔ THE LEGS MUST BE COMBINABLE WITH EACH OTHER, and only the operator can
      // say so. Its bet-builder re-requests the board with the current selection in
      // `selectedStakeIds` (captured live 2026-09-12: the app fires that POST on
      // every tap) and the reply is the set of stakes that can still JOIN it. Two
      // outcomes from our own board are not a combination: "Обе забьют Да + Наполи"
      // may be priced as a pair, or refused, or priced differently — the second call
      // is the only thing that knows.
      // `BBF` on the reply is the operator's OWN combined price for exactly this
      // selection, and 0 is its refusal. ⛔ Never multiply the legs instead: these
      // outcomes are correlated and the book prices them lower — measured 1.85 ×
      // 2.14 = 3.96 where the operator says 3.05.
      // The render pass already priced this card (`priceBetBuilderCards`) — reuse it
      // rather than spending a second authenticated call on the tap.
      const preview = (s as unknown as Record<string, unknown>).bb_factor;
      const bbFactor = typeof preview === "number"
        ? preview
        : ((await priceViaService(eventId, s.is_live === true, bblegs.map((l) => l.stakeId)))
          ?? (await catalog.betBuilderFactor(eventId, s.is_live === true, bblegs.map((l) => l.stakeId))));
      // ⛔ THE SAME RULES THE RENDER PASS APPLIES, APPLIED AGAIN HERE. A card can reach
      // the tap unpriced (the render call 401'd, or the page had not hydrated) and
      // then be added at a price worse than one of its own legs — measured on a real
      // tap 2026-09-12, Обе-Да 1.93 + Тотал 1.73 went into the slip at 1.73.
      // ⚠️ The pair call is skipped when the price IS the render pass's: that pass
      // has already run the triple-against-its-pair check on this same card, so a
      // card that got here with `bb_factor` set is one the book pays for.
      const bestLegOdds = Math.max(...bblegs.map((l) => Number(l.odds) || 0));
      const pairFactor = (bblegs.length >= 3 && typeof preview !== "number")
        ? await bbPairFactor(eventId, s.is_live === true, bblegs.map((l) => l.stakeId))
        : null;
      const why = bbVerdict(bbFactor, bestLegOdds, pairFactor, bblegs.length);
      if (why) {
        diagLastAdd = `bet-builder:${why} @${bbFactor}`;
        reportBuilder(why, {
          legs_matched: bblegs.length, board_stakes: board.stakes.length,
          bb_factor: bbFactor, best_leg: bestLegOdds, pair_factor: pairFactor,
        });
        return { added: false, navigated: false };
      }
      // ⚠️ null is "could not tell" (slow or missing reply), NOT a refusal — blocking
      // on it would drop the card for every visitor whose second call was slow.
      // ⛔⛔ THE ACCA GOES IN AS A BET-BUILDER ITEM, NEVER AS LOOSE LEGS. The standard
      // slip refuses same-event legs: measured 2026-09-12, two legs of one fixture
      // posted with `toggleStake` reported `posted=2 landed=2` on our side while the
      // operator's coupon kept exactly ONE. That refusal is the whole reason the book
      // has a bet builder, so this path uses it — see bet-builder-app.ts.
      const bbAdd = await addBetBuilderToCoupon(
        eventId, s.is_live === true, bblegs.map((l) => l.stakeId),
      );
      const priced = bbFactor ?? combinedFactor(bblegs.map((l) => l.odds ?? 1));
      if (bbAdd.landed < bblegs.length) {
        diagLastAdd = `bet-builder:${bbAdd.reason} ${bbAdd.landed}/${bblegs.length}`;
        reportBuilder(bbAdd.reason, {
          legs_matched: bblegs.length, legs_posted: bblegs.length, legs_landed: bbAdd.landed,
          board_stakes: board.stakes.length, combined_odds: priced,
        });
        return { added: false, navigated: false };
      }
      diagLastAdd = `bet-builder:in-slip ${bbAdd.landed} legs @${priced}`;
      legacyWatcher.recordAdd(s, rank);
      busWatcher.recordAdd(s, rank);
      renderer.markAdded(id);
      renderer.minimize();
      noteAdded(id);
      refresh();
      client.sendEvent("", "betslip_add", id, null, {
      brief: briefAtTap,
        event_id: s.event_id, rec_type: s.rec_type, booked: false,
        add_verdict: "bet-builder-in-slip",
        advice_snapshot: adviceSnapshot(s),
        legs: eventLegs(s.legs), legs_posted: bblegs.length, legs_landed: bbAdd.landed,
        // the OPERATOR's price when it gave one, ours only as a fallback — and both,
        // so the gap between them is measurable rather than argued about
        combined_odds: priced,
        multiplied_odds: combinedFactor(bblegs.map((l) => l.odds ?? 1)),
        operator_priced: bbFactor != null,
        dims: dimsOf(s, rank),
      });
      return { added: true, navigated: false };
    }

    const rawLegs = (s.legs && s.legs.length)
      ? s.legs.map((l) => ({ stakeId: Number(l.stake_id), eventId: Number(l.event_id) }))
      : [{ stakeId: Number(s.stake_id), eventId: Number(s.event_id) }];
    const stakes = rawLegs.filter(
      (x) => Number.isFinite(x.stakeId) && x.stakeId > 0 && Number.isFinite(x.eventId) && x.eventId > 0,
    );
    const isCombo = !!(s.legs && s.legs.length);
    const complete = stakes.length === rawLegs.length && stakes.length > 0;

    // Re-read every leg against the freshest board BEFORE posting — the state we
    // are actually deciding on. The pool a card was ranked from is re-pulled every
    // 45s and an event lingers up to 100s, so a live line can be suspended while
    // the card still looks freshly validated (owner, 2026-08-05: two failed adds,
    // both on runaway favourites — the first line a book pulls). Without this the
    // strip cannot tell a stale pick apart from a post the operator refused, and
    // those are opposite fixes.
    const legOdds = new Map<number, number | null>();
    if (s.legs && s.legs.length) {
      for (const l of s.legs) legOdds.set(Number(l.stake_id), l.odds ?? null);
    } else {
      legOdds.set(Number(s.stake_id), s.odds);
    }
    const probe = stakes.map((x) => {
      const expected = legOdds.get(x.stakeId) ?? null;
      const st = catalog.stakeState(x.eventId, x.stakeId, expected);
      return { stakeId: x.stakeId, state: st.state, expected, odds: st.odds, ageMs: st.ageMs };
    });

    // One resolution in flight per suggestion. The renderer disables the button
    // (renderer.ts, pointerEvents:none) but a poll-driven re-render can replace
    // that button mid-flight, and two overlapping flows for one pick can each
    // decide to retract — the second one re-inserting what the first swept out.
    // The guard belongs here, with the state it protects, not in the DOM.
    if (addInFlight.has(id)) {
      diagLastAdd = formatAddProbe(probe, "in-flight", 0, 0);
      return { added: false, navigated: false };
    }
    addInFlight.add(id);
    // Stamp the tap BEFORE any work, so even a throw leaves the right pick named.
    diagLastAdd = formatAddProbe(probe, "posting", 0, 0);
    let added = false;
    let landedCount = 0;
    let verdict: AddVerdict | "not-posted" = "not-posted";
    let postedCount = 0;
    /** How confirmAdd reached its verdict (declined + operator status / timeout /
     *  ack) — the field that lets the dashboard split "the operator said no"
     *  from "nobody answered in 2,500 ms". Null when nothing was posted. */
    let addResolution: AckResolution | null = null;
    /** Legs of this pick an EARLIER add already owns. legs_landed counts the
     *  coupon's answer over posted+alreadyIn, so it can exceed legs_posted —
     *  this is the missing term that makes the two comparable. */
    let alreadyInCount = 0;
    /** Legs swapped from a dead stake id onto the board's current one. */
    let repaired = 0;
    // Dead stake id → the stake the repair re-posted for the SAME outcome. The
    // late-ack watcher watches the ids it was started with; without this map a
    // repaired leg never "lands" under its old id and the whole acca is swept
    // (Chrome QA 2026-10-13: a 5-leg add, 2 legs repaired, coupon emptied 21 s
    // later as `partial_swept`).
    const successors = new Map<number, number>();
    /** Every stake id this pick holds in the coupon — what the slip-hold
     *  tracker watches once the add lands. */
    let heldIds: number[] = [];
    try {
      // Ask the operator what their coupon really holds BEFORE deciding which legs
      // are already in it. Our own `added` set is written at confirm time and was
      // only ever cleared by a placed bet, so a coupon the user emptied by hand
      // left phantoms that skip a leg, force a `partial` verdict on an id nobody
      // will ack, and get the landed legs swept back out. Null means this operator
      // has no bus at all (Hannibalbet) — there an empty read is not evidence.
      const coupon = busWatcher.couponStakeIds();
      if (coupon) sink.reconcileAdded(coupon);
      if (complete && !(s.legs && s.legs.length) && typeof s.part === "string" && s.part && catalog.subEventIdForStake(Number(s.stake_id)) == null) {
        try { await catalog.fetchBetBuilderStakes(Number(s.event_id), false); } catch { /* the add then posts nothing and falls back */ }
      }
      if (stopped) return { added: false, navigated: false };
      const addStartedAt = Date.now();
      const outcome = complete ? sink.addStakes(s, (stakeId) => catalog.subEventIdForStake(stakeId)) : null;
      if (outcome) {
        postedCount = outcome.posted.length;
        alreadyInCount = outcome.alreadyIn.length;
        const allIds = [...outcome.posted, ...outcome.alreadyIn].map((x) => x.stakeId);
        heldIds = allIds;
        // A longer verdict window where the ack channel is KNOWN to exist.
        // Parikara's traffic is mobile Turkmenistan — an AddStake round trip
        // there can outlive the 2,500 ms that is right for the no-bus decision
        // (Hannibalbet must still fall through to its mint quickly).
        let capturedResolution: AckResolution | null = null;
        verdict = await busWatcher.confirmAdd(
          allIds, busWatcher.detected() ? ACK_TIMEOUT_BUS_MS : 2500,
          (r) => { capturedResolution = r; },
        );
        if (stopped) return { added: false, navigated: false };
        // Read the resolution NOW — the repair path's own confirmAdd below
        // overwrites it, and this add's telemetry must describe this add.
        addResolution = capturedResolution ?? busWatcher.lastAddResolution();
        // The mint is Hannibalbet-only evidence; only consult it where the
        // operator has no ack channel at all.
        const mintOk = verdict === "no-bus"
          ? (await catalog.mintBooking(stakes, isCombo)) != null
          : false;
        if (stopped) return { added: false, navigated: false };
        // Ask the OPERATOR what it actually holds, then let the pure resolver
        // decide. Every list it returns is a subset of what THIS call posted —
        // see add-resolution.ts for why that invariant is load-bearing.
        const r = resolveAdd({
          outcome,
          landedIds: busWatcher.landedStakes(allIds),
          verdict,
          mintOk,
        });
        let lateWatching = false;
        const repairedPosted = new Set<number>();
        const repairedPairs = new Map<number, StakePair>();
        const currentOutcome = () => ({
          posted: outcome.posted.filter((p) => !successors.has(p.stakeId)
            || repairedPosted.has(successors.get(p.stakeId)!))
            .map((p) => repairedPairs.get(successors.get(p.stakeId)!) ?? p),
          alreadyIn: outcome.alreadyIn.concat(outcome.posted
            .filter((p) => successors.has(p.stakeId) && !repairedPosted.has(successors.get(p.stakeId)!))
            .map((p) => repairedPairs.get(successors.get(p.stakeId)!) ?? p)),
        });
        const watchLate = () => {
          if (lateWatching) return;
          lateWatching = true;
          const waitBeforeLate = Math.max(0, Date.now() - addStartedAt);
          const watchedIds = [...allIds];
          const watchedVerdict = verdict;
          busWatcher.watchLanded(watchedIds, LATE_ACK_WINDOW_MS, (settle) => {
            if (stopped) return;
            const allLanded = settle.allLanded;
            const r2 = resolveAdd({
              outcome: currentOutcome(),
              landedIds: settle.landed,
              verdict: allLanded ? "confirmed" : watchedVerdict,
            });
            sink.confirmAdded(r2.confirm);
            if (r2.added) sink.confirmAdded(currentOutcome().alreadyIn);
            sink.forget(r2.forget);
            if (r2.retract.length) {
              busWatcher.expectRemoval(r2.retract.map((p) => p.stakeId));
              sink.retractStakes(r2.retract, s);
            }
            if (r2.added) {
              // The add DID land — the card said "couldn't add" wrongly. Flip
              // it and register influence exactly as the confirmed path does.
              legacyWatcher.recordAdd(s, rank);
              busWatcher.recordAdd(s, rank, successors);
              renderer.markAdded(id);
              renderer.minimize();
              noteAdded(id);
              refresh();
              slipHold.hold({
                id, card: s, label: holdLabel(s), ...holdLegs(s),
                odds: (s.combined_odds as number | undefined) ?? s.odds ?? null,
                stakeIds: watchedIds.map((sid) => successors.get(sid) ?? sid),
                eventId: s.event_id ? String(s.event_id) : undefined,
              });
            }
            // Report only when the late window CHANGED the story — a rejected
            // add that stayed rejected has nothing new to say. late_ms is the
            // real Turkmen ack latency (verdict window + time to settle) and is
            // the number that should set ACK_TIMEOUT_BUS_MS, not a guess.
            if (settle.landed.length > 0) {
              client.sendEvent("", "betslip_add_late", id, null, {
                event_id: s.event_id,
                rec_type: s.rec_type,
                first_verdict: watchedVerdict,
                late_verdict: allLanded ? "confirmed" : "partial_swept",
                late_ms: waitBeforeLate + settle.elapsedMs,
                legs_landed_late: settle.landed.length,
                legs_posted: watchedIds.length,
                // Why a sweep happened (2026-10-13: 9 sweeps, 4 with a repaired
                // leg): how many legs were repaired onto a successor, how many of
                // those successors are in the coupon, and the stake states of the
                // legs that were still missing.
                legs_repaired: successors.size,
                successors_landed: [...successors.values()].filter((sid) =>
                  busWatcher.landedStakes([sid]).length > 0).length,
                legs_missing: watchedIds.filter((sid) => settle.landed.indexOf(sid) < 0
                  && !(successors.has(sid)
                       && busWatcher.landedStakes([successors.get(sid)!]).length > 0)).length,
                dims: dimsOf(s, rank),
              });
            }
          }, (sid) => successors.get(sid) ?? sid);
        };
        if (addResolution.how === "timeout" && verdict !== "confirmed") {
          // ⛔ A TIMEOUT verdict is a snapshot, not an answer — and acting on it
          // is what turned slow acks into damage: sweeping a partial acca at
          // 2.5s while the third ack was still in flight left the user holding
          // exactly the legs we removed the others from (§2's "34 accas dropped
          // half their healthy legs"). So on timeout we DEFER every mutation:
          // no confirm, no forget, no retract — the legs stay `posted`, which
          // also blocks a re-tap from double-toggling them meanwhile. The
          // watcher below settles the moment every leg lands, or at the window
          // end, and only THEN applies the resolver's decision to the real
          // coupon. An explicit decline still acts immediately — the operator
          // answered, there is nothing to wait for.
          added = false;
          landedCount = r.landedCount;
          watchLate();
        } else {
          sink.confirmAdded(r.confirm);
          if (r.added) sink.confirmAdded(outcome.alreadyIn);
          sink.forget(r.forget);
          if (r.retract.length) {
            busWatcher.expectRemoval(r.retract.map((p) => p.stakeId));
            sink.retractStakes(r.retract, s);
          }
          added = r.added;
          landedCount = r.landedCount;
        }

        // REPAIR: legs the operator accepted and immediately flagged `Deleted`.
        //
        // A stake id names a price, not an outcome. When Digitain pulls a market
        // and re-opens it the outcome returns under a NEW id, and a coupon holding
        // the old one is not merely stale — the row greys under "Доступность
        // событий изменилась" and the Принять button cannot revive it. Our pool is
        // re-pulled every 45s and an event lingers 100s, so we post exactly those
        // ids. Reproduced live 2026-08-13 by posting an id seen leaving the feed
        // 35s earlier: the coupon took it and killed it.
        //
        // Scoped to ids from THIS call, so a leg the user let go dead by hand is
        // never touched. Swap only to the SAME outcome — never a different one.
        const dead = busWatcher.deadStakes(outcome.posted.filter((p) => sink.ownsLeg(p)).map((p) => p.stakeId));
        if (dead.length) {
          sink.removeDeadStakes(outcome.posted.filter((p) => dead.some((d) => d.stakeId === p.stakeId)), s);
          const fresh: Array<{ stakeId: number; eventId: number }> = [];
          for (const d of dead) {
            const live = catalog.resolveOutcome({
              eventId: d.eventId,
              marketId: d.marketId,
              code: d.code,
              argument: d.argument,
            });
            // No successor is a real answer, not a failure to try: markets like
            // "Исход с 1-50 мин." expire outright (10 of 10 vanished stakes in one
            // live sample had none). Then the leg is simply gone and we say so.
            if (live) {
              fresh.push({ stakeId: Number(live.stakeId), eventId: d.eventId });
              successors.set(d.stakeId, Number(live.stakeId));
            }
          }
          repaired = fresh.length;
          if (fresh.length) {
            const re = sink.addStakesByPair(fresh, s);
            if (re) {
              for (const p of [...re.posted, ...re.alreadyIn]) repairedPairs.set(p.stakeId, p);
              for (const p of re.posted) repairedPosted.add(p.stakeId);
              const reIds = [...re.posted, ...re.alreadyIn].map((x) => x.stakeId);
              await busWatcher.confirmAdd(reIds);
              if (stopped) return { added: false, navigated: false };
              const landedNow = busWatcher.landedStakes(reIds);
              sink.confirmAdded(re.posted.filter((p) => landedNow.indexOf(p.stakeId) >= 0));
              heldIds = allIds.map((sid) => successors.get(sid) ?? sid);
              landedCount = busWatcher.landedStakes(heldIds).length;
              added = landedCount === heldIds.length;
              if (added) { sink.confirmAdded(currentOutcome().alreadyIn); verdict = "confirmed"; }
              else watchLate();
            }
          }
        }

        // REPAIR: an outright DECLINE that names a stake the board has moved on from.
        //
        // The same rotated-id defect as the `Deleted` repair above, one step earlier in
        // its life: the book re-opens the market under a NEW stake id and the operator
        // refuses the old one outright (`ERROR_STAKE_DOS_NOT_EXIST` / 1304) instead of
        // taking it and greying the row. Nothing landed, so nothing the user holds can
        // be toggled — which is the only licence `toggleStake`'s symmetry needs.
        //
        // ⛔ `declined` ONLY. `rejected` is also what a TIMEOUT resolves to, and a
        // timeout means nobody answered, not that the ids are wrong. Re-posting a leg
        // the coupon already holds REMOVES it (`toggleStake` is symmetric), so acting
        // on silence is how the widget would write bets nobody chose.
        if (verdict === "rejected" && addResolution?.how === "declined") {
          const postedIds = new Set(outcome.posted.map((p) => p.stakeId));
          const refused = legIdentities(s).filter((l) => postedIds.has(l.stakeId));
          const fresh = planRepost(refused, (o) => catalog.resolveOutcome(o));
          if (fresh.length) {
            const re = sink.addStakesByPair(fresh, s);
            if (re) {
              for (const p of [...re.posted, ...re.alreadyIn]) repairedPairs.set(p.stakeId, p);
              for (const old of refused) {
                const replacement = planRepost([old], (o) => catalog.resolveOutcome(o))
                  .find((p) => fresh.some((f) => f.stakeId === p.stakeId && f.eventId === p.eventId));
                if (replacement) successors.set(old.stakeId, replacement.stakeId);
              }
              for (const p of re.posted) repairedPosted.add(p.stakeId);
              const reIds = [...re.posted, ...re.alreadyIn].map((x) => x.stakeId);
              await busWatcher.confirmAdd(reIds);
              if (stopped) return { added: false, navigated: false };
              const landedNow = busWatcher.landedStakes(reIds);
              sink.confirmAdded(re.posted.filter((p) => landedNow.indexOf(p.stakeId) >= 0));
              heldIds = allIds.map((sid) => successors.get(sid) ?? sid);
              landedCount = busWatcher.landedStakes(heldIds).length;
              added = landedCount === heldIds.length;
              if (added) { sink.confirmAdded(currentOutcome().alreadyIn); verdict = "confirmed"; }
              else watchLate();
              repaired += re.posted.length;
              // The coupon holds the REPAIRED ids, not the refused ones — the slip
              // hold (and its price nudge) must watch what is actually in the slip.
            }
          }
        }
      }
    } catch (err) {
      // The renderer swallows a throw from here and prints "не удалось добавить"
      // with no telemetry and no probe line — so an add that died on an exception
      // was indistinguishable from one the operator refused, and invisible in the
      // stats. Record it as itself.
      const msg = err instanceof Error ? err.message : String(err);
      diagLastAdd = formatAddProbe(probe, `ERROR ${msg}`, postedCount, landedCount);
      client.sendEvent("", "betslip_add", id, null, {
      brief: briefAtTap,
        event_id: s.event_id,
        rec_type: s.rec_type,
        booked: false,
        add_verdict: "error",
        add_error: msg,
        legs_posted: postedCount,
        legs_landed: landedCount,
        stake_states: probe,
        dims: dimsOf(s, rank),
      });
      return { added: false, navigated: false };
    } finally {
      addInFlight.delete(id);
    }
    // Navigate fallback — never injects a stale/unconfirmed pick. `add()` returns the
    // target event id ONLY if it really delivered the navigation, and null otherwise
    // (Parikara is native DOM: SportFrame.elem is null, so every SportFrame command
    // is a silent no-op there). We must pass that answer through untouched — the CTA
    // says "Opened in sportsbook" off it, and inferring a navigation from a failed
    // add is what put that claim over a page that never moved.
    const navTarget = added ? null : sink.add(s);
    const navigated = navTarget != null;
    // Arm BOTH sources for this add — only one of them will ever fire (see the
    // feature-detect note above), and neither knows yet which operator this is.
    if (added) {
      legacyWatcher.recordAdd(s, rank);
      busWatcher.recordAdd(s, rank, successors);
    }
    // We took them to the match: from here they pick their own market, so record
    // the influence against the EVENT. `sink.add` returns the target id ONLY when
    // the navigation was really delivered (null on Hannibalbet's dead SportFrame
    // path, null when the router could not be reached), and that null is exactly
    // what stops a navigation we never performed from being claimed as influence.
    if (navTarget) busWatcher.recordNavigate(s, Number(navTarget), rank);
    // Let the cross-origin betslip render the toggled legs before the card flips to
    // "✓ In betslip" — otherwise it can read as "added" a beat before it visibly appears.
    if (added) {
      await new Promise((r) => setTimeout(r, 600));
      if (stopped) return { added: false, navigated: false };
      // Retire the card FIRST: minimize() computes the launcher line from the
      // still-visible picks, so retiring after it briefly headlined the very pick
      // that was just sent to the slip.
      renderer.markAdded(id);
      // The overlay and Digitain's betslip occupy the same right edge, so the expanded
      // widget covers the slip (and its Book a Bet/Accept controls) it just filled.
      // Collapse to the pill; the real slip is the source of truth from here on.
      renderer.minimize();
      // Tell the server it is in the slip, and refresh, so the freed slot is
      // backfilled with the next-best pick instead of a shrinking board.
      // ⛔ `noteAdded`, NOT `exclude` — see the two-list comment above. On the
      // open match an add must hold the chip, and a skip must release it.
      noteAdded(id);
      refresh();
      // The pick is now sitting unplaced in their coupon: the pill holds it
      // (live price, one optional nudge) until a placement or a removal.
      slipHold.hold({
        id, card: s, label: holdLabel(s), ...holdLegs(s),
        odds: (s.combined_odds as number | undefined) ?? s.odds ?? null,
        stakeIds: heldIds,
        eventId: s.event_id ? String(s.event_id) : undefined,
      });
    }

    diagLastAdd = formatAddProbe(probe, String(verdict), postedCount, landedCount);

    client.sendEvent("", "betslip_add", id, null, {
      brief: briefAtTap,
      event_id: s.event_id,
      rec_type: s.rec_type,
      legs: eventLegs(s.legs),
      combined_odds: s.combined_odds,
      booked: added,
      add_verdict: verdict,
      // How the verdict was reached: `declined` (with the operator's own status
      // string in add_status) vs `timeout` vs `ack`. `rejected` alone could not
      // say which failure the ~25% live rejection rate actually is.
      add_ack: addResolution ? addResolution.how : undefined,
      add_status: addResolution ? addResolution.status : undefined,
      legs_posted: postedCount,
      // ⚠️ Counted over posted + already-in ids against the operator's whole
      // coupon, so it can exceed legs_posted; legs_already_in is the term that
      // reconciles them. Do not build a landing rate from legs_landed alone.
      legs_landed: landedCount,
      legs_already_in: alreadyInCount,
      // ⛔ WHETHER THE APP'S OWN COUPON WAS READABLE AT ALL (`coupon-store.ts`).
      // `unreadable` means the slice path is wrong for this app build, and the
      // verdict fell back to the bus alone — the one fact that says whether the
      // second reading above is doing anything on this operator. Reported, never
      // guessed: "we could not look" and "the slip was empty" are different.
      coupon_store: storeCouponStakeIds(window) === null ? "unreadable" : "read",
      // Legs the operator killed on arrival (`Deleted`) that we swapped onto the
      // board's current stake id. Non-zero means our pool handed out an id that had
      // already rotated — the rate here is the direct measure of how stale the 45s
      // pull is in practice, and the thing to watch if picks start dying.
      legs_repaired: repaired,
      // The board's own answer for each leg at post time — live / moved / gone /
      // no-event / unknown, with the age of the reading. This is what makes the
      // failed adds diagnosable off-device instead of one screenshot at a time.
      stake_states: probe,
      // Distinguishes "we failed and left the user where they were" from "we failed
      // but did take them to the pick".
      navigated,
      // A saved snapshot represents a successful card outcome only: either the
      // pick landed in the coupon or the fallback really opened its match.
      ...((added || navigated) ? { advice_snapshot: adviceSnapshot(s) } : {}),
      dims: dimsOf(s, rank),
    });
    return { added, navigated };
  };

  // ⛔ TWO LISTS, BECAUSE THEY ARE OPPOSITE INSTRUCTIONS.
  //
  // Both stop the backend re-serving that CARD, but on the match the visitor is
  // actually looking at they mean contrary things: a skip is "show me something
  // else", an add is "I backed this — stay here". While both rode `dismissed_ids`
  // the server could not tell them apart, so an add dropped the open match from
  // the pool entirely, the ranking pin had nothing to re-add, `is_current` was
  // never stamped, and the launcher chip rotated away to an unrelated fixture
  // seconds after the bet went in. Measured live 2026-08-31 on Барселона–Райо.
  //
  // Exclusion is ranking-only and carries no engagement signal either way: an
  // added pick is scored purely by its `betslip_add` / `bet_placed` events.
  // ⛔ SKIPS ARE PERSISTED, ADDS ARE NOT. The SPA remounts this module on every
  // route change; a skip that dies with the instance is re-served within
  // seconds (see skip-memory.ts for the measurement). An add is re-derived from
  // the operator's own coupon, so it does not need a store.
  const skips = new SkipMemory(client.getSessionId());
  const addedIds: string[] = [];
  const remember = (list: string[], id: string): void => {
    if (!list.includes(id)) list.push(id);
    if (list.length > 100) list.shift();
  };
  const exclude = (id: string): void => skips.add(id);
  const noteAdded = (id: string): void => remember(addedIds, id);
  renderer.primeDismissed(skips.ids());

  if (!collector && diagEnabled()) {
    startDiag(theme.sportsbook_root, () => ({
      pollAgeMs: diagPollTs == null ? null : Date.now() - diagPollTs,
      pool: diagPool,
      suggestions: diagSugg,
      chipDisplay: renderer.debugChipDisplay(),
      lastAdd: diagLastAdd,
      favourites: favourites.diagLine(),
      starGate: starGateLine(readStarGate()),
      betHistory: betHistory.diagLine(),
      // `skips.getSessionId()`, NOT `client.getSessionId()`: the cookie
      // hard-expires at 30 minutes with no refresh-on-read, so on a page view
      // that outlives it the cookie has rotated while SkipMemory still reads
      // and writes the key it was constructed with. Naming the live cookie
      // here would print a session prefix the `skips` count does not belong
      // to — a diagnostic that lies is worse than none.
      skips: `${skips.ids().length} (session ${skips.getSessionId().slice(0, 8)})`,
      hold: slipHold.active() ? renderer.debugSlipHold() : undefined,
    }), apiKey);
  }

  // Skip = "show me something else": exclude it, report the dismiss, and refresh
  // right away so a replacement pick appears instead of an emptying widget.
  renderer.onDismiss = (id: string) => {
    const rank = currentSuggestions.findIndex((x) => x.id === id);
    const s = rank >= 0 ? currentSuggestions[rank] : undefined;
    exclude(id);
    client.sendEvent("", "dismiss", id, null, {
      event_id: s?.event_id,
      rec_type: s?.rec_type,
      dims: s ? dimsOf(s, rank) : undefined,
    });
    refresh();
  };

  // Throttle /suggestions calls. The SportFrame bus dispatches an event on every
  // live-board tick, so an un-throttled refresh hits ~1 req/s and creeps toward the
  // backend's 60/min rate limit (and burns LLM cycles). Cap to one poll per
  // MIN_REFRESH_MS with a trailing call so the latest browsed state still lands.
  const MIN_REFRESH_MS = 2500;
  let lastRefreshStart = 0;
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;
  const refresh = (): void => {
    ensureSportsbookSource();
    if (stopped) return;
    // ⛔ Closed by the user. Stop polling THIS page view too, not just the next
    // load: /suggestions re-sends the whole board on every tick, and capacity —
    // not per-request cost — is what binds on this pilot. A widget nobody can see
    // must not keep paying for itself.
    if (measurementOnly || holdoutHidden || renderer.isHidden()) return;
    // ⛔ The server retired this build. Guarded on the THROTTLE side too, not only
    // the idle timer: the SportFrame bus drives this path on every board tick, so
    // a retired build left un-guarded here would keep polling for the whole session
    // even with its timer stopped.
    if (client.isRetired()) return;
    const since = Date.now() - lastRefreshStart;
    if (since < MIN_REFRESH_MS) {
      if (trailingTimer == null) {
        trailingTimer = setTimeout(() => { trailingTimer = null; if (!stopped) refresh(); }, MIN_REFRESH_MS - since);
      }
      return;
    }
    lastRefreshStart = Date.now();
    void runRefresh();
  };

  /** ⭐ The open fixture's ladder just arrived — POST IT NOW. Without this the
   *  ladder waits for the next tick, and a match page's ticks are sparse
   *  (measured on parikara 2026-09-18: +2.1s, +11.1s, +42.1s, +127.2s after the
   *  match opened). The chooser needs those lines to build anything past the
   *  moneyline, so a same-match acca on that fixture cannot exist until they are
   *  posted — the candidate arrives when the reader has already gone.
   *  ⛔ Once per fixture per page view, and never on an empty arrival: a cached
   *  prime resolves instantly, and polling on every resolution would loop. */
  const ladderLanded = (id: string): void => {
    if (!id || ladderRepolled.has(id)) return;
    if (catalog.marketsFor(id).length === 0) return;
    ladderRepolled.add(id);
    refresh();
  };

  // ⭐ NAVIGATION IS THE TRIGGER, not the next 30s tick. Measured 2026-09-09
  // with the 400ms DOM probe in product/docs/QA-widget-cases-2026-09-09.md
  // (case 4): 24.5s to name the open match, 38.7s to the add button on
  // basketball, because detection ran only inside the idle poll and the first
  // poll after a navigation only PRIMED the fixture's prices.
  //
  // ⛔ The observed GetEvent id is a HINT, never the truth: the app fires
  // GetEvent for its own featured super-tip cards too (open-match-net.ts,
  // Round 3 note — Aston Villa–Arsenal pinned in the lobby, 2026-08-31). So the
  // fetch only WAKES us; we act when the app's own store says that id is the
  // open event. Two looks, 300ms and 1500ms apart, then give up until the next
  // fetch — the store fills a beat after the response lands.
  let openEventTimer: ReturnType<typeof setInterval> | null = null;
  const cleanupTimers = new Set<ReturnType<typeof setTimeout>>();
  const trackedTimeout = (fn: () => void, ms: number): void => {
    const timer = setTimeout(() => { cleanupTimers.delete(timer); if (!stopped) fn(); }, ms);
    cleanupTimers.add(timer);
  };
  // ⛔ One wake per open (open-wake.ts): both watchers below fire for the same
  // fixture, and each used to schedule its own pin poll — a duplicate post
  // 2.4 s after every open. Same fixture inside one poll interval = same open.
  const wakeOnOpen = makeOpenWaker({
    // ⭐ POLL WHEN THE LADDER IS IN HAND, not only 800ms after asking for it.
    // The 800ms timer stays — it carries the PIN, which is what puts the
    // fixture on screen — but the ladder decides whether the chooser can build
    // anything beyond the moneyline, and on a match page the next poll can be
    // 85 seconds away (measured 2026-09-18: +2.1s, +11.1s, +42.1s, +127.2s).
    // ⛔ Bounded by `ladderRepolled`, one extra poll per fixture per page view.
    prime: (id) => catalog.primeMarkets([id]),
    onLadder: (id) => { if (!stopped) ladderLanded(id); },
    poll: () => { if (!stopped) refresh(); },
    after: (fn, ms) => trackedTimeout(fn, ms),
    now: () => Date.now(),
  }, 800, () => client.pollIntervalMs(5000));
  onOpenMatchObserved((id) => {
    const act = () => {
      if (stopped || appOpenEventId() !== id) return false;
      wakeOnOpen(id);
      return true;
    };
    trackedTimeout(() => { if (!act() && !stopped) trackedTimeout(act, 1200); }, 300);
  });
  // ⛔ The fetch hook never fires on Parikara — the match page comes over the
  // sportfeed WebSocket (measured 2026-09-09: still 26s to the pin with the hook
  // alone). The store is the signal that always exists; watch it directly.
  openEventTimer = watchOpenEvent(() => appOpenEventId(), wakeOnOpen, 500);

  let inFlight = false;
  let retryStale = false;
  // ⛔ ONE deck-page fetch at a time, on its own flag rather than `inFlight`. A
  // swipe near the end of the deck must not be swallowed by whatever 2.5s poll
  // happens to be in the air — and equally, two fast swipes must not both ask
  // for page N. `Deck.needsMore()` stays true, so a refused fetch is retried by
  // the next swipe rather than lost.
  let deckInFlight = false;
  const runRefresh = async (deckReq?: { page: number; seen: string[] }): Promise<void> => {
    if (deckReq) {
      if (deckInFlight) return;
      deckInFlight = true;
    } else {
      if (inFlight) return;
      inFlight = true;
    }
    const reqLocale = locale; // snapshot: a switch mid-flight makes this response stale
    try {
      const pool = catalog.getCandidates();       // OTHER live events to recommend
      // A cold/empty live catalogue must not suppress the server's shared
      // pre-match board. It survives SDK swaps and API restarts independently
      // of this visitor's feed; keep polling it at the server's normal TTL.
      if (pool.length === 0 && operatorConfig.prematch_pool !== true) {
        // …but never a skeleton under a reader's thumb: a deck-page fetch that
        // lands on an empty pool just fails quiet and is retried by the next swipe.
        if (!deckReq) renderer.showLoading();     // graceful skeleton, not a blank screen
        else renderer.deckPageFailed();
        return;
      }

      let current = source.getMatches()[0] ?? null;  // event being browsed
      // ⛔ FALLBACK FOR A NATIVE-DOM OPERATOR. `getMatches()` is fed by the
      // SportFrame bus, which only speaks when the sportsbook runs in an IFRAME
      // or the operator forwards events to BidCanvasCompanion.ingest. Parikara
      // does neither, so current_context was null on EVERY request from that
      // operator and the backend's 1.6 browsed-event boost could never fire —
      // observed live 2026-08-31, a visitor on Lecce v Roma was shown women's
      // tennis. detectOpenMatch reads the page and accepts a result ONLY when it
      // matches exactly one board entry, so it restores the open match without
      // ever being able to name the wrong one.
      // The id of an open fixture we could not match to any client board —
      // exact (the operator's own GetEvent request), sent for the SERVER to
      // validate against its live board + pre-match cache. See resolveOpenMatch
      // for why this is allowed out unvalidated while the DOM reader is not.
      let contextOnlyId: string | null = null;
      if (!current) {
        // Two independent readings, network first: the app FETCHES the event it
        // opens, and an id is exact where page text is a guess. Validated
        // against BOTH boards this client holds — the live pool and the
        // pre-match board (2026-08-31: a visitor on pre-match Atalanta–Bologna
        // was shown a US Open tennis chip, because only the live pool was ever
        // consulted).
        const r = resolveOpenMatch(
          pool as unknown as BoardEntry[],
          catalog.prematchBoard() as unknown as BoardEntry[],
        );
        if (r.entry) current = r.entry as unknown as typeof current;
        else contextOnlyId = r.contextOnlyId;
      }
      // ⛔ DIAGNOSTIC: why open-match detection fires or not. store_id / reachable
      // tell us whether the pre-match match populates eventDetails.eventList.
      if (diagEnabled()) console.log("[bidcanvas] open-match", JSON.stringify({
        store_id: appOpenEventId(), store_reachable: appStoreReachable(),
        resolved: current ? String((current as { id?: unknown }).id ?? "") : null,
        context_only: contextOnlyId,
      }));
      // ⛔ THE OPEN MATCH ARRIVES WITH NO PRICES. Measured 2026-08-31 on Lecce
      // v Roma: the live board entry carries `markets: []` while the operator's
      // own page shows a full book, so the backend has nothing to build a card
      // from and the fixture on screen loses to anything that has prices —
      // proven server-side, the same request WITH a match-result market returns
      // the open match ranked first.
      //
      // ⛔ FRESH LINES ONLY. cachedMatchResultFor enforces the same 30s TTL as
      // the ladder: pushing a price from two hours ago is worse than pushing
      // none. The fetch is one cycle behind by construction, which is the same
      // contract the ladder already has.
      if (current) {
        const cid = String((current as { id?: unknown }).id ?? "");
        const cm = (current as { markets?: unknown[] }).markets;
        if (cid && (!Array.isArray(cm) || cm.length === 0)) {
          const fresh = catalog.cachedMatchResultFor(cid);
          if (fresh.length > 0) {
            current = { ...(current as object), markets: fresh } as typeof current;
          } else {
            void catalog.primeMarkets([cid]);
          }
        }
      }
      // ⭐ PRIME THE OPEN PRE-MATCH FIXTURE. The prime above only covers a
      // `current` we could match to a board entry; a fixture resolved as
      // `contextOnlyId` never had its ladder fetched, so `marketsFor` would
      // stay empty forever and the chooser would only ever see its moneyline.
      // ⛔ GATED ON THE FLAG: a prime is a real GetEvent request against the
      // operator's API, and `facts_pick_market` is the operator's switch for
      // that cost. (The prime just above is not gated — it fires only for a
      // `current` whose own board entry arrived unpriced.)
      // ⛔ NOT gated on the flag any more (2026-09-09): without the ladder the open
      // fixture's card has no price and renders as a "worth watching" card ON
      // ITS OWN PAGE. One GetEvent per opened fixture is the whole cost.
      // ⛔ AND THE OPEN LIVE FIXTURE, EVERY POLL (measured on parikara
      // 2026-09-10, Ганза–Фюрт open): its board entry is priced, so the prime
      // above never fires; the ladder from the open-match click expired after
      // MARKETS_TTL_MS (30s) and every later poll posted the fixture with NO
      // lines — the server's tier lines (facts or price-only) need the ladder,
      // so the open match fell back to one card after two polls. One GetEvent
      // per 30s for the fixture on screen is the whole cost.
      // ⛔ REFRESHED BEFORE IT EXPIRES, not after. A prime that waits for
      // `marketsFor` to come back empty fires at the poll that already went
      // out without lines, so polls alternated ladder / no ladder (measured
      // 23:44:43 → 23:45:14). Half the TTL: the ladder posted is at most one
      // poll old and never past the 30s the stake ids are trusted for.
      const openLadderId = contextOnlyId
        || (current ? String((current as { id?: unknown }).id ?? "") : "");
      if (openLadderId) {
        // ⛔ ONLY WHEN WE POSTED THIS POLL WITHOUT A LADDER. A prime that
        // resolves from the cache resolves instantly, so refreshing on every
        // resolution would poll in a loop; refreshing only when the fixture had
        // no lines at build time, once per fixture, cannot.
        const hadLadder = catalog.marketsFor(openLadderId).length > 0;
        void catalog.primeMarkets([openLadderId], OPEN_LADDER_MAX_AGE_MS)
          .then(() => { if (!hadLadder) ladderLanded(openLadderId); }, () => {});
      }
      // The SportFrame bus reports the current event's league/sport in the USER'S
      // language only, so the similar-league boost would stop recognising it on a
      // French/Arabic board. The same event is in the feed pool, which carries the
      // always-English copy — join on the event id and pass both.
      const poolMatch = current ? pool.find((p) => p.id === current.id) : undefined;
      // A pre-match `current` is not in the live pool; its own entry carries the
      // always-English copy, so fall back to it before giving up.
      const enSource = poolMatch ?? (current as unknown as { league_en?: string; sport_en?: string } | null);
      const currentContext = current
        ? {
            event_id: current.id,
            league: current.league,
            sport: current.sport,
            league_en: enSource?.league_en ?? "",
            sport_en: enSource?.sport_en ?? "",
          }
        : contextOnlyId
          ? { event_id: contextOnlyId, league: "", sport: "", league_en: "", sport_en: "" }
          : null;
      let openFixtureId: string | null = null;
      let openFixtureIsLive = false;

      const suggestions = await client.fetchSuggestions(
        [],
        {
          // ⛔ The NORMALISED ladder only — 2,496 B for the committed 20-line
          // fixture, measured 2026-08-31 — never the raw GetEvent response.
          // Cache-only: a market we have not primed yet is simply absent,
          // which the backend reads as no opinion.
          live_matches: (() => {
            // ⛔ The OPEN fixture's ladder is read with a grace past the 30s
            // TTL: the poll and the TTL are both 30s, so a ladder fetched right
            // after one poll was 30.0s old at the next and read as expired
            // (measured 23:47:55 → 23:48:26). It is refreshed every poll (see
            // OPEN_LADDER_MAX_AGE_MS), so what is posted is ≤ one poll old.
            const budget = { left: MAX_LINE_EVENTS };
            return pool.map((m) => {
              const { scored_lines, ...rest } = m as LiveMatch & { scored_lines?: MarketLine[] };
              const got = postedLadder(String(m.id), scored_lines,
                                       catalog.marketsFor(String(m.id)), lastCovered, budget,
                                       catalog.subEventLinesFor(String(m.id)));
              return (got
                ? { ...rest, event_markets: got.lines, event_markets_source: got.source }
                : rest) as unknown as Record<string, unknown>;
            });
          })(),
          odds_snapshot: [],
          betslip: [],
          // ⭐ THE OPEN PRE-MATCH FIXTURE'S LADDER. A fixture that is open but
          // not on the live board was never posted with lines — the ladder is
          // only ever attached to entries of `live_matches` — so the chooser
          // could see its moneyline and nothing else. Posted under its own key
          // so the backend merges it for the pinned event only.
          // ⛔ THE TEST IS "CAN THE SERVER SEE IT", NOT "DID WE FAIL TO RESOLVE IT".
          // These two used to fire only for `contextOnlyId` — the case where no
          // board of ours describes the fixture. But a PRE-MATCH match page
          // resolves fine against `catalog.prematchBoard()`, and `live_matches` is
          // the LIVE pool only, so the fixture the visitor is staring at was posted
          // nowhere: `current_context` named it, `page_context` never carried it,
          // and the server has no candidate to pin. Measured 2026-09-12 on
          // Наполи–Болонья — `current_context.event_id 40343484`, no `open_event`,
          // and the whole tier/express path (every same-match acca) silently skipped.
          ...(() => {
            const openId = current
              ? String((current as { id?: unknown }).id ?? "") || null
              : contextOnlyId;
            openFixtureId = openId;
            openFixtureIsLive = Boolean(openId && pool.some(
              (m) => String((m as { id?: unknown }).id ?? "") === openId));
            if (!openId) return {};
            // already in `live_matches`? the server can see the match, but its
            // live feed has no English names; send only the store's names.
            if (openFixtureIsLive) {
              const names = openEventNamesFor(openId, appOpenEvent());
              return names ? { open_event: names } : {};
            }
            const out: Record<string, unknown> = {};
            const oem = openEventMarketsFor(openId, (id) => catalog.marketsFor(id),
                                            (id) => catalog.subEventLinesFor(id, SUB_WARM_INTERVAL_MS));
            if (oem) out.open_event_markets = oem;
            // the store's own event object first (freshest 1X2), else the board
            // entry we resolved — either one names the fixture exactly.
            const oe = openEventEntryFor(openId, appOpenEvent(), (id) => catalog.marketsFor(id),
                                         (id) => catalog.subEventLinesFor(id, SUB_WARM_INTERVAL_MS))
              ?? (current as unknown as Record<string, unknown> | null) ?? undefined;
            if (oe) out.open_event = oe;
            return out;
          })(),
        },
        locale,
        device,
        {
          current_context: currentContext,
          // ⛔ Read here, not in `refresh`: this payload is built in its own closure.
          route: readRouterLocation(sportsbookRoot()),
          browse_history: browseHistory,
          dismissed_ids: skips.ids(),
          added_ids: addedIds,
          favourites: favourites.snapshot() as unknown as Record<string, unknown> | null,
          bet_history: betHistory.profile() as unknown as Record<string, unknown> | null,
          // ⭐ THE SWIPE DECK'S PAGING. The API discriminates on the PRESENCE of
          // `deck_page`, so the ordinary poll sends page 0 with no cursor — it
          // asks for the head of the deck, which is what it has always wanted.
          // `seen_ids` is the cursor: a page is the first 12 cards not in it.
          //
          // ⛔ MOBILE ONLY, and the keys are ABSENT otherwise — on the desktop
          // panel there is no swipe, so a deck of twelve would be twelve
          // impressions a poll for a surface that lists three. Absence is what
          // keeps that path on the legacy response.
          ...(renderer.deckMode()
            ? {
              deck_page: deckReq ? deckReq.page : 0,
              seen_ids: deckReq ? deckReq.seen : [],
            }
            : {}),
        },
      );

      if (stopped) return;

      // Soak/diagnosis telemetry: pool health + what the backend returned.
      // A pool stuck at ≤1 means the full-feed template died (KB §8 symptom).
      console.log(`[bidcanvas] pool=${pool.length} suggestions=${suggestions ? suggestions.length : "FAIL"} excluded=${skips.ids().length} added=${addedIds.length}`);

      if (suggestions == null) {      // failed poll — keep current DOM, don't claim freshness
        // ⛔ A failed PAGE backs off. Each chevron tap at the end re-asked at once,
        // so a shed 503 at peak became one request per tap (parikara 2026-10-19).
        if (deckReq) renderer.deckPageFailed();
        return;
      }
      if (reqLocale !== locale) {
        // Language switched while this poll was in flight: the prose is in the OLD
        // locale. Don't render it — re-poll in the new locale instead.
        retryStale = true;
        return;
      }
      // A later deck page is an APPEND, never a redraw: the reader is mid-swipe,
      // the cursor must not move, and none of the poll's other work (the open
      // fixture, the ladder warm, impressions for cards nobody has reached yet)
      // belongs to it.
      // ⭐ PRICE THE SAME-MATCH ACCAS BEFORE ANYBODY SEES THEM. Their legs are
      // correlated, so the product of the legs is not the price: measured on the live
      // book 2026-09-12, П1 1.85 + Больше2.5 2.14 pays 3.05, not 3.96, and the 3-leg
      // with Обе-Да pays 4.15, not 7.80. Showing the product would advertise a return
      // 30-88% higher than the operator will actually pay.
      await priceBetBuilderCards(suggestions);
      if (stopped) return;
      // Price the pre-match combos held back above, off the render path — they
      // surface on a later poll once a verdict lands (see bb-space.ts).
      // A failed pass is retried on the next poll.
      // ⭐ …AND SHOW IT WITHOUT WAITING FOR THE NEXT TICK. The first browser on a
      // fixture has no shared verdict to seed from (`seedServed` covers the ~51%
      // of accepted combos where another session primed the fixture within 10
      // minutes, measured 2026-09-18), so for the other half the accepted card
      // appears only on whatever poll comes next — and on a pre-match match page
      // that can be the 30s idle tick, long after the reader has gone.
      // ⛔ BOUNDED: at most ONE extra poll per fixture per page view, and only
      // when the drain produced something SHOWABLE. A refusal changes nothing on
      // screen, and this is a request against the operator's own API.
      // ⛔ `bbShow` IS THE BOUND. Marking every ok fixture and then refreshing
      // whenever the drain finishes spends a poll on the operator's API for a
      // drain that produced nothing showable — which is what the comment above
      // forbids, and `bbRepolled` became a set nothing reads.
      let bbShow: string | null = null;
      const drained = drainSpace(bbQueue, bbKey, bbSpace, bbPriceOne, (c, k, e) => {
        if (stopped) return;
        if (e.verdict === "ok") {
          const eid = String(c.legs?.[0]?.event_id ?? "");
          if (eid && !bbRepolled.has(eid)) bbShow = eid;
        }
        bbEmitVerdict(c, k, e);
      });
      // NOT awaited: `drainSpace` is a serial round-trip per queued card against
      // the operator's API, and awaiting it here put that whole fan-out in front
      // of `renderer.updateDeck` below (d03b327). The follow-up poll rides the
      // promise instead, so the render never waits for it.  It must start on a
      // later task: a cached/operator-fast price can settle before this poll's
      // `finally` clears `inFlight`, and a direct `refresh()` would then be
      // silently discarded. That left accepted pre-match cards held forever.
      void drained.then(() => {
        if (stopped || !bbShow) return;
        bbRepolled.add(bbShow);
        trackedTimeout(() => refresh(), 0);
      }, () => undefined);
      if (!deckReq && openFixtureId) {
        const warmFixtureId = openFixtureId;
        const userId = catalog.betBuilderGate ? catalog.betBuilderGate().userId : null;
        const loggedIn = typeof userId === "number" && userId > 0;
        const now = Date.now();
        if (shouldWarmSubLines({
          openId: warmFixtureId,
          isLive: openFixtureIsLive,
          loggedIn,
          have: catalog.subEventLinesFor(warmFixtureId, SUB_WARM_INTERVAL_MS).length,
          lastTriedAt: subLinesWarmTriedAt.get(warmFixtureId),
          now,
        })) {
          subLinesWarmTriedAt.set(warmFixtureId, now);
          // ⛔ ONE authenticated request per open pre-match fixture per ten minutes per logged-in tab,
          // made on purpose because the board is never fetched otherwise (28 add taps in 14 days,
          // 0 corners/cards singles shown).
          void catalog.fetchBetBuilderStakes(Number(warmFixtureId), false).then(() => {
            if (stopped || subLinesWarmed.has(warmFixtureId)
              || catalog.subEventLinesFor(warmFixtureId, SUB_WARM_INTERVAL_MS).length === 0) return;
            subLinesWarmed.add(warmFixtureId);
            void refresh();
          }, () => undefined);
        }
      }
      if (deckReq) {
        const d = client.lastDeck();
        // ⛔ CONCAT ONLY IF IT LANDED. The peek can close while a page is in
        // flight, taking the deck with it — growing `currentSuggestions` with
        // cards the renderer dropped would leave the dims/impression lookups
        // resolving against a list nobody is being shown.
        if (renderer.appendDeckPage(suggestions, d?.has_more ?? false, d?.page ?? deckReq.page)) {
          currentSuggestions = currentSuggestions.concat(suggestions);
        }
        return;
      }
      if (stopped) return;
      currentSuggestions = suggestions;
      if (diagEnabled()) {
        const s0 = suggestions.find((x) => x.home && x.away && x.event_id);
        if (s0) {
          const b = catalog.prematchBoard().find((m) => String(m.id) === String(s0.event_id)) as { starts_at?: string } | undefined;
          console.log("[bidcanvas] starts_at", s0.event_id, s0.starts_at, b?.starts_at ?? null);
        }
      }
      diagPollTs = Date.now();
      diagPool = pool.length;
      diagSugg = suggestions.length;
      // Collector: the /suggestions call already warmed odds_history server-side.
      // Skip the render + impression telemetry (no viewer, no analytics pollution).
      if (collector) return;
      // ⭐ Tell the renderer which fixture is OPEN, from the SDK's own detection
      // (`current` / `contextOnlyId`): its cards render the ADD, not "open match".
      renderer.openMatchEventId = current
        ? String((current as { id?: unknown }).id ?? "") || null
        : contextOnlyId;
      renderer.updateDeck(suggestions, client.lastDeck()?.has_more ?? false);
      qualifiedSelfOpen.poll(suggestions);
      // ⛔ WARM THE LADDER for what we just showed, so the NEXT poll can let
      // the facts pick a market. Ranking is server-side, so this is the only
      // point at which we know which events matter — and the pool is 50+
      // events, far too many to fetch. One cycle behind, by construction.
      //
      // ⛔ GATED ON THE FLAG. Each call is a real request against the operator's
      // API for a large response, so with the feature off this would be pure
      // cost to Digitain for a ladder nothing reads. The flag is absent from the
      // config until the control panel sets it, and absent means off.
      lastCovered = coveredForLines(suggestions, client.lastCoveredLive());
      if (factsPickMarket) {
        // ⛔ COVERED EVENTS ONLY. The backend marks the fixtures it actually
        // has facts for; priming the rest is a request against the operator's
        // API for a ladder nothing can read.

        catalog.primeMarkets(eventsToPrime(suggestions));
      }
      // Legacy intro/momentum self-open stays dormant. The qualified controller
      // above is the only poll-driven opening and requires both operator gates.
      lastTopId = nextTopId(suggestions, lastTopId);
      // An `impression` must mean the pick was ON SCREEN. While the panel is folded
      // into the launcher the cards are behind it, so reporting them inflated the
      // funnel with picks nobody ever saw — and made impression→add unreadable for
      // every start_collapsed operator. The launcher's own visibility is now carried
      // by pill_impression instead.
      if (renderer.isCollapsed()) return;
      // Once per suggestion per IMPRESSION_RESEND_MS — not per poll. Re-sending
      // on every 2.5-5s refresh was ~1-2 req/s per viewer (each doubled by the
      // CORS preflight) into nginx's 2r/s zone, and it inflated the funnel's
      // impression side for picks the user was already looking at. Same
      // dedup + fields as a peek impression — see sendImpression above.
      // ⛔ ON-SCREEN cards only — see renderer.visibleSheetIds. The rest report
      // when they are scrolled into view (onSheetScroll below).
      const onScreen = renderer.visibleSheetIds();
      suggestions.forEach((s, rank) => { if (onScreen.has(s.id)) sendImpression(s, rank); });
    } finally {
      if (deckReq) {
        deckInFlight = false;
      } else {
        inFlight = false;
        if (retryStale) {
          // The discarded response left the board un-refreshed — poll again now in
          // the new locale (also covers the watchLocale refresh() that no-oped on
          // the inFlight guard while the stale poll was running).
          retryStale = false;
          if (!stopped) trackedTimeout(() => { void runRefresh(); }, 0);
        }
      }
    }
  };

  // ⭐ THE SWIPE DECK'S THREE HOOKS (2026-09-09).
  //
  // The deck is three cards from its end and the server said there is more.
  // `seen_ids` is the cursor — the next page is the first cards not in it.
  renderer.onNeedMore = () => {
    void runRefresh({ page: renderer.deckNextPage(), seen: renderer.deckSeenIds() });
  };
  // A card swiped past inside the dwell window. Reported once, when the peek
  // closes, and never re-reported: the renderer drops the deck on close.
  // `method: "soft"` keeps it apart from a pressed Skip — the signal is real but
  // weaker, and a dashboard that mixed them would read a swipe as a rejection.
  renderer.onSoftSkips = (cards) => {
    for (const s of cards) {
      // ⛔ The OPEN fixture's own lines are never soft-skipped (owner 2026-09-10:
      // "all we can show for current match"). Paging past over 2.5 to reach the
      // win used to dismiss over 2.5, BTTS and both expresses for the session.
      const open = renderer.openMatchEventId;
      if (open && String((s as { event_id?: unknown }).event_id ?? "") === open) continue;
      const rank = currentSuggestions.findIndex((x) => x.id === s.id);
      exclude(s.id);
      // ⛔ AND OUT OF THE RENDERER'S OWN LIST, not just the server's exclusions.
      // `exclude()` only reaches the NEXT poll's request; a re-open before that
      // (2.5-5s away) rebuilt the deck from `lastSuggestions` and dealt the same
      // card again — a second fast swipe then sent a SECOND soft dismiss for it,
      // and the skip count read double.
      renderer.dismiss(s.id);
      client.sendEvent("", "dismiss", s.id, null, {
        method: "soft",
        event_id: s.event_id,
        rec_type: s.rec_type,
        dims: rank >= 0 ? dimsOf(currentSuggestions[rank], rank) : undefined,
      });
    }
  };
  // Fifty forward swipes without a take. `renderer.onHide("deck_exhausted")`
  // fires straight after this and does the hiding — this event is the reason,
  // and it carries how much was dealt to get there.
  renderer.onDeckExhausted = (swipes, served) => {
    client.sendEvent("", "deck_exhausted", "", null,
      { swipes, cards_served: served, ...ictx });
  };

  // Start the hidden-widget candidate pool, and refresh on each browsed event.
  catalog.start();

  // PRE-MATCH POOL. The candidate pool has always been live-only — measured over
  // three days to 2026-08-27, all 27,083 card impressions were live events — so a
  // top-tier fixture a day out was never out-ranked; it was never a candidate.
  // Between 03:00 and 07:00 UTC no top-tier football is live anywhere, and 80-91%
  // of what the widget showed then was amateur filler.
  //
  // Gated per operator and OFF by default: the pull is a fan-out of real requests
  // against the OPERATOR'S production API, so they decide whether we make it at
  // all — the same rule as `bet_history_backfill`. The board goes to
  // /api/companion/prematch on its own 5-minute clock, never onto the 5s
  // /suggestions payload.
  //
  // ⛔ AND SAMPLED. The board is IDENTICAL for every visitor and the server
  // caches it for 15 minutes, so it needs one poster every few minutes — not
  // every session running the whole tournament fan-out against the operator's
  // API. `prematch_pull_share` is that dial, as a percentage of sessions.
  //
  // ⚠️ Decided ONCE, at boot, and never re-rolled: a per-tick draw would make a
  // session that pulls at 12:00 skip at 12:05, so the board would be posted by
  // a different browser each time with no continuity, and a session that had
  // already paid for the fan-out would drop the cheap follow-up polls.
  //
  // The DEFAULT is 100 (every session), which is exactly the behaviour that
  // shipped — so this arriving changes nothing until somebody turns it down,
  // and turning the scope up without turning this down is the mistake it exists
  // to prevent.
  const pullShare = typeof operatorConfig.prematch_pull_share === "number"
    ? Math.max(1, Math.min(100, operatorConfig.prematch_pull_share)) : 100;
  const pullsPrematch = Math.random() * 100 < pullShare;
  let prematch: PrematchSource | null = null;
  if (!measurementOnly && operatorConfig.prematch_pool === true && pullsPrematch) {
    prematch = new PrematchSource(
      () => catalog.fetchPrematchBoard(
        HORIZON_MINUTES,
        operatorConfig.prematch_scope === "all" ? "all" : "popular",
        typeof operatorConfig.prematch_tournaments === "number"
          ? operatorConfig.prematch_tournaments : undefined,
      ),
      // `locale` is READ AT POST TIME, not captured when the source is built:
      // the user can switch language mid-session, and a board labelled with the
      // language they have left would be filed for readers it does not match.
      (board) => client.postPrematchBoard(board, locale),
    );
    prematch.start();
    // So a placed leg on a fixture that has not started can be recognised as
    // one. The catalog holds the live board; this hands it the other half.
    catalog.prematchBoard = () => prematch!.getBoard();
  }
  source.start(() => { recordBrowse(); void refresh(); });
  // Fallback poll (also covers first-load timing + catalog readiness). Follows
  // the server's ttl for the same reason as the visible widget's timer above.
  const idlePollTimers = new Set<ReturnType<typeof setTimeout>>();
  const scheduleIdlePoll = (): void => {
    if (stopped || client.isRetired()) { if (!stopped) retireWidget(renderer); return; }
    if (upgradeWidget(client, renderer)) return;
    const timer = setTimeout(() => {
      idlePollTimers.delete(timer);
      if (!stopped) { void refresh(); scheduleIdlePoll(); }
    }, client.pollIntervalMs(5000));
    idlePollTimers.add(timer);
  };
  scheduleIdlePoll();

  // Follow an in-session language switch (EN → FR → AR). Not in collector mode:
  // there is no viewer, and the headless session has no language to follow.
  const localeStop = !collector ? watchLocale(locale, (next) => {
    locale = next;
    client.setLocale(locale);
    renderer.setLocale(next);
    catalog.onLocaleChange(next);
    void refresh();
  }, operatorConfig.locale) : () => {};
  // ⛔ THE STARTER REGISTERS ITS OWN TEARDOWN. `retireWidget` calls
  // `activeWidgetCleanup`, and that handle used to be assigned ONLY by `init()`
  // — so every test drove `stop()` directly and the one production path into
  // the teardown had no cover at all. Registering here means the retire path is
  // the same function whoever started the widget.
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (activeWidgetCleanup === stop) activeWidgetCleanup = null;
    localeStop();
    for (const timer of idlePollTimers) clearTimeout(timer);
    idlePollTimers.clear();
    for (const timer of starGateTimers) clearTimeout(timer);
    starGateTimers.clear();
    if (openEventTimer != null) clearInterval(openEventTimer);
    for (const timer of cleanupTimers) clearTimeout(timer);
    cleanupTimers.clear();
    if (trailingTimer != null) clearTimeout(trailingTimer);
    trailingTimer = null;
    stopOpenMatchObserved();
    prematch?.stop();
    source.stop();
    catalog.stop();
    favourites.stop();
    betHistory.stop();
    legacyWatcher.stop();
    busWatcher.stop();
    if (tapRoot) tapRoot.removeEventListener("pointerdown", onSportsbookTap, true);
    for (const timer of lifecycleIntervals) clearInterval(timer);
    lifecycleIntervals.clear();
    holdout?.stop();
    sportsbookTime.stop();
    document.removeEventListener("visibilitychange", flushSportsbookTime);
    window.removeEventListener("blur", flushSportsbookTime);
    window.removeEventListener("focus", sportsbookTime.tick);
    window.removeEventListener("pagehide", pauseSportsbookTime);
    window.removeEventListener("pageshow", resumeSportsbookTime);
    document.removeEventListener("freeze", pauseSportsbookTime);
    document.removeEventListener("resume", resumeSportsbookTime);
    renderer.destroy();
  };
  activeWidgetCleanup = stop;
  return stop;
}

// Auto-init
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

/**
 * Which events the SDK may prime a market ladder for.
 *
 * ⛔ COVERED EVENTS ONLY, and exported so the SUITE can test the real filter.
 * The version of this that lived inline in the poll loop was mirrored by a test
 * that re-typed the same expression, so `sofa_covered` could have been renamed
 * on either side and the whole feature would have gone dark with a green suite.
 * Each prime is a real GetEvent request against the operator's API; priming a
 * fixture the fact layer has no recording for buys a ladder nothing can read.
 */
/** How old the OPEN fixture's ladder may be at poll time before it is re-fetched. */
// Probe 2026-09-10: a 15s refresh parsed ~2 MB every 15s and froze the renderer;
// the 90s read grace makes the 30s fetch TTL enough for a ladder on every poll.
const OPEN_LADDER_MAX_AGE_MS = 30_000;

/** Live events whose live-feed lines the NEXT poll may post: the served fact
 *  cards plus every fact-covered live candidate the server named (served or not).
 *  Found live 2026-09-17: served-only gating opened for almost no event. */
export function coveredForLines(suggestions: Suggestion[], coveredLive: string[]): Set<string> {
  return new Set([...eventsToPrime(suggestions), ...coveredLive]);
}

export function eventsToPrime(suggestions: Suggestion[]): string[] {
  return suggestions
    .filter((s) => s.sofa_covered)
    .map((s) => String(s.event_id ?? ""));
}

/** Events per poll that may carry the live-feed lines slice. Body measured on
 *  the box 2026-09-16: p50 82 KB, max 202 KB; ~2 KB per event × 12 keeps the
 *  worst poll under ~230 KB. */
export const MAX_LINE_EVENTS = 12;

export function postedLadder(
  matchId: string, scored: MarketLine[] | undefined, cached: MarketLine[],
  covered: Set<string>, budget: { left: number }, sub: MarketLine[] = [],
): { lines: MarketLine[]; source: "get_event" | "live_feed" } | null {
  // ⭐ Corners/cards ride whichever ladder is already being posted. They are
  // harvested from a bet-builder reply the SDK made anyway (see
  // `subEventLinesFrom`) and cost no request of their own.
  // ⛔ They NEVER create a post on their own: a fixture with no ladder still
  // posts nothing, so this cannot spend the `budget` or widen the poll body on
  // a fixture the backend was not going to score.
  if (cached.length) return { lines: sub.length ? cached.concat(sub) : cached, source: "get_event" };
  if (!scored || !scored.length || !covered.has(matchId) || budget.left <= 0) return null;
  budget.left -= 1;
  return { lines: sub.length ? scored.concat(sub) : scored, source: "live_feed" };
}
